/* eslint-disable eqeqeq */
/**
 * Phase 6 Vendor-Port W6 — FAN selector static data + pure builders (§33.1).
 *
 * Every constant + builder here is VERBATIM from
 * /tmp/ksdm-phase2/django/static/mvc/component_selector/components/fan/view/
 * fan.selector.js (6,249 — the LARGEST selector). Kept out of the component so
 * FanSelector.tsx reads cleanly. Mirrors phxSelectorData (the W4 pattern); the
 * fan dependency graph rooted at mfg_id is the LARGEST consumer of
 * useDependencyGraph.
 *
 * The vendor envelope + curve-fetch payloads + the selectFan write shape are
 * kept PURE here so a verifier can read the exact write shapes (the §32 byte
 * diff) without running the component.
 */

import type { DependencyGraphSpec } from './useDependencyGraph';
import { fanCmsOptionListByPath, type FanCmsDoc } from './fanCms';

const MASTER = '/api/v1.0/master/v1.0.0/ahu';
const CMS = '/api/v1.0/cms/1/v1.0.0/ahu';

/** the fan CMS params doc (legacy fn['current.fan.cms'] — fan.cms.js urlRoot). */
export const FAN_CMS_DOC_URL = `${CMS}/fans/params/`;
/** spring-isolator CMS doc (legacy fn['spring.isolator.cms']). */
export const FAN_SPRING_ISOLATOR_CMS_URL = `${CMS}/fans/isolators/spring/params/`;
/** motor-base CMS doc (legacy fn['motor.base.cms'] — motor.base.cms.js:7
 * urlRoot '/api/v1.0/cms/1/v1.0.0/ahu/motors/bases/params/'. The previous
 * `${CMS}/fans/motor/base/params/` 404'd, leaving the mtr_base_* rate values
 * unresolvable — P1-verify fix, 2026-07-09). */
export const FAN_MOTOR_BASE_CMS_URL = `${CMS}/motors/bases/params/`;
/** rating notes (legacy NotesCollection — fan/model/notes.js). */
export const FAN_NOTES_URL = `${CMS}/fans/notes/`;
/** per-fan-type mfg settings — legacy FanSettings (fan.cms.setting.js:7-11
 * builds `<urlRoot><type>_arr<arr>/mfg/settings/`), read by updateQty for the
 * max_qty_wide / max_qty_high ranges behind Qty Wide x High. */
export const fanMfgSettingsUrl = (type: string, arr: string) =>
  `${CMS}/fans/${type}_arr${arr}/mfg/settings/`;
/** pricing methods — a GLOBAL master list (legacy
 * fan/collection/pricing_methods.js:8). The Pricing tab iterates it; the port
 * hardcoded three options instead. Same route CoilSelector already uses. */
export const FAN_PRICING_METHODS_URL = `${MASTER}/pricing_methods/`;
/** inlet-guard CMS (legacy CmsInletGuard). */
export const FAN_INLET_GUARD_CMS_URL = `${CMS}/fans/inlet_guard/params/`;
/**
 * the fan MOUNTINGS master collection (legacy `self.mountings = new Mountings()`
 * → `component.fan.mountings`, fetched at fan.selector.js:591-594 and used as
 * `CUSTOM_COLLECTIONS.mounting_id = Object.values(self.mountings.toJSON())` in
 * mergeIdsWithCMS (js:2099)). Rows are `{id, name:"Horizontal",
 * value:"horizontal"}` — the NAME is the option text and the VALUE is the vendor
 * code the rate envelope carries (`"mounting":"horizontal"`, discovery §3.2).
 * Used by BOTH hosts (see FanSelector loadParams) — legacy's mergeIdsWithCMS is
 * one code path and does not branch on the host.
 */
export const FAN_MOUNTINGS_URL = `${MASTER}/fan_mountings/`;
/** spacing selects (legacy getFanSpacings 6050-6072). */
export const FAN_SPACING_SIDES_URL = `${MASTER}/fan/spacing/sides/`;
export const FAN_SPACING_TOP_BTM_URL = `${MASTER}/fan/spacing/top_btm/`;
/**
 * Performance-tab "System Effect" options (legacy model/fan.system_effect.js —
 * `new SystemEffect({ fan_type_id: 4 })` at fan.selector.js:625; the fan_type_id
 * is HARDCODED to 4). The `{{#each system_effect}}` list in fan.selector.tmpl:557.
 */
export const FAN_SYSTEM_EFFECT_URL = `${CMS}/fans/params/system_effect/?fan_type_id=4`;

/** the fan/motor catalogue smart-list base urls (CMSPages — §31). */
export const FAN_CATALOG_BASE_URL = `${CMS}/fans/all/`;
export const MOTOR_CATALOG_BASE_URL = `${CMS}/motors/all/`;

/** air streams collection (legacy fn['current.air.cms'] air_stream node). */
export const FAN_AIR_STREAMS_URL = `${CMS}/air/air_streams/`;

/**
 * §32 FAN MFG-id → manufacturer (copy verbatim — fan.selector.js:151-161
 * RATING_AVAILABLE + MFG_MAPPING). Re-exported from ratingEnvelope so the
 * selector imports it from one place, but kept named here for the spec checklist.
 */
export { FAN_MFG_MAPPING, FAN_FUNCTION_NAME, FAN_PLUG_TYPE_MFGS } from './ratingEnvelope';

/** fan.selector.js:106-142 — the params_fan reach-in allow-list (verbatim). */
export const ALLOWED_FAN_PARAMS = new Set<string>([
  'mfg_id', 'type_id', 'series_id', 'style_id', 'arr_id', 'rotation_id',
  'qty_wide', 'qty_high', 'wheel_mtl_id', 'cube_housing_id', 'frequency',
  'motor_controller', 'airflow_per_fan', 'airflow_actual', 'tsp',
  'max_peak_pressure_percent', 'elevation', 'fan_inlet_pressure',
  // Dev-sync H (Annexair, mfg 237): fan_inlet_temp_wb + mtr_safety join the
  // legacy allowed_fan_params (fan.selector.js:139/:151 on merged dev).
  'fan_inlet_temp', 'fan_inlet_temp_wb', 'fan_inlet_vane_id',
  'fan_disch_diffuser', 'amca_class_id',
  'wheel_size_id', 'wheel_width_percent_id', 'wheel_type_id',
  'wheel_dia_percent_id', 'percent_of_peak_id', 'wheel_dia_nominal',
  'wheel_dia_nominal_id', 'mtr_safety', 'motor_type_id', 'redundancy_id',
  'speed_condition_id', 'housed_discharge_id',
]);

/** fan.selector.js:486-492 — isolation defaults (verbatim). */
export const FAN_ISOLATION_DEFAULTS: Record<string, number> = {
  isolator_type_id: 1,
  isolator_mfg_id: 99,
  isolator_series_id: 178,
  specified_deflection: 19,
  fan_isolators_supplied_by_id: 1,
};

/** fan.selector.js:483-484 — the canvas coefficients (load-bearing). */
export const CANVAS_COEFFICIENT_X = 1.5;
export const CANVAS_COEFFICIENT_Y = 1.3;

// ---------------------------------------------------------------------------
// ★ Phase 6.5 (builder 3) — THE ZERO-STATE FIELD PRESETS.
//
// The header "Independent Rating" popup is the app's only ZERO-STATE entry into
// the fan selector (no AHU, no model — `modelId 0`, `headerMode true`). Legacy
// pre-fills a dozen-plus selects on a first-ever open; four distinct legacy
// mechanisms produce that state, all of them ported below and ALL of them
// applied ONLY in header mode (see FanSelector's applyHeaderZeroState):
//
//   1. `setDefaults()`  fan.selector.js:3169-3411 — the per-MANUFACTURER preset
//      literals. Called at construction for a fan with no master_fan_id
//      (:1448, which in header mode is ALWAYS, since there is never a
//      master_fan_id) and again on every mfg_id / motor_type_id change
//      (:1461-1463 via the state listener + :4766 in changeParams).
//   2. `mergeIdsWithCMS()` state side effects, run from renderSelects for every
//      re-rendered select: the per-mfg overrides :2182-2220, the isolation
//      defaults :2222-2239 (from `isolation_defaults` :491-497), the
//      mounting_method_id rule :2240-2246 and the motor_position_id sole-option
//      rule :2247-2255.
//   3. `renderSelects()` :7644-7654 — the UNGATED sole-option auto-select
//      (`mergedData[param].length === 1 → state.set(param, …)`). This one is
//      NOT subject to the dependency handler's `setDefaultsIfNullChecker`
//      (:1215-1218 → DEFAULT_FIELDS), which is why fields outside
//      DEFAULT_FIELDS (motor_type_id, speed_condition_id, amca_class_id,
//      wheel_mtl_id, mounting_id, fan_isolators_supplied_by_id …) still end up
//      selected on a first open.
//   4. the browser's own implicit selection of a placeholder-less `<select>`:
//      renderSelects only emits the leading `--` option for the fields in
//      `selectboxesWithLineOptions` (:7392-7419), and legacy `collectData`
//      RATES THE DOM (`elem.val()`, :4248-4259) — so a null state value on a
//      placeholder-less select is still rated. (1)+(2)+(3) put the same values
//      in state, which is what the React state-driven envelope needs.
// ---------------------------------------------------------------------------

/**
 * ★ Qty Wide x High `--` — the manufacturers whose Qty Wide / Qty High selects
 * offer a blank option, and default to it.
 *
 * Picking `--` means "rate every array up to CMS's Max Qty Wide x High and let
 * me choose from the results": the vendor handler fans out over the whole grid
 * and the rated-outputs table grows a Wide x High select per fan model (see
 * `AnnexairFanHandler._qty_combinations` and the `wide_high` column in
 * FanSelector.tsx). Nothing else in the dialog changes.
 *
 * A vendor is added here ONLY together with backend support: `--` against a
 * handler that still forwards the raw value sends the vendor a blank qty and
 * rates nothing. The backend side is `AnnexairFanHandler` for 237 and
 * `qty_expansion.EXPANDABLE_MANUFACTURERS` for the rest.
 *
 * Every rating manufacturer, as of 2026-10-09. What `--` COSTS varies by two
 * orders of magnitude, though, because it is one vendor call per array and the
 * vendor API does not scale with concurrency — measured single-rate times:
 *
 *   237 Annexair      0.1s  |  64  Twin City Fan  ~9s
 *   37  ebm-papst     0.6s  |  209 New York Blower ~9-13s
 *   23  Comefri       2.2s  |  67  Ziehl-Abegg    23s
 *   164 Rosenberg     6s    |  47  Greenheck      ~9s
 *   221 Lau = the Twin City Fan backend. 152 BasX is not offered on this box.
 *
 * So the left column covers a whole grid inside the backend's wall-clock
 * budget and the right column runs out of it partway. Either way the rating
 * ends up covering the grid: the counts the budget could not reach come back
 * as `wide_high_pending` and the selector hands them back to the same fan-out
 * after the response (`rateRestOfGrid`), which on the slow vendors takes
 * minutes and several round trips rather than one request -- and the models
 * are drawn only once all of them have answered. See qty_expansion.py.
 */
export const FAN_QTY_UNSET_MFGS = new Set<number>([
  237, 64, 209, 37, 23, 164, 47, 67, 221, 152,
]);


/** The state/`<option>` value behind the Qty Wide x High `--` option.
 *
 * The empty string, not null: a `<select>` whose `value` matches no `<option>`
 * renders its FIRST option instead of a blank (the React-vs-jQuery trap that
 * shipped in #1791), so the blank has to be a real `<option value="">` and the
 * state has to hold exactly what that option's value is. `toNum` already maps
 * '' (and '--') to null, so every numeric consumer sees "unset". */
export const FAN_QTY_UNSET = '';

/** Is a Qty Wide / Qty High value the `--` option rather than a number? */
export const fanQtyIsUnset = (v: unknown): boolean =>
  v == null || v === '' || v === '--' || v === 'None';

/**
 * Is the PAIR on `--`, i.e. is there no fan array at all?
 *
 * Either half being unset is enough — the combined select moves both together,
 * so a half-unset pair can only come from stored data, and half an array is no
 * array. Everything that divides the total airflow by the array (and the
 * Airflow-per-fan box itself) is meaningless while this is true, so the rule
 * lives here rather than being spelled out at each of the five call sites.
 */
export const fanQtyPairIsUnset = (wide: unknown, high: unknown): boolean =>
  fanQtyIsUnset(wide) || fanQtyIsUnset(high);

/**
 * ★ …and every manufacturer OPENS on `--` (Anna, 2026-10-08): it is the
 * DEFAULT Qty Wide x High, not just an option.
 *
 * It was Annexair alone until now, on the argument that a vendor costing ~10s
 * a call should not START on a whole-grid rating. Anna asked for the default
 * everywhere with that cost understood — so a routine Greenheck or Ziehl-Abegg
 * rate now fans out, spends the backend's wall-clock budget on the arrays it
 * can reach and reports how many it skipped, unless the user picks an array
 * before rating.
 *
 * Derived from FAN_QTY_UNSET_MFGS rather than kept as a second list, so the
 * default can never name a manufacturer whose select has no `--` option to
 * land on: that select would render its FIRST option (1 x 1) while state said
 * "unset" — the #1791 mismatch — and the two lists cannot drift apart.
 *
 * Applied at the three points where a manufacturer lands on the state: the
 * mount seed (board + header first open), a Manufacturer change, and Reset.
 * Null for a manufacturer with no `--` to default to.
 */
export const fanQtyDefaultsFor = (
  mfg: unknown
): { qty_wide: string; qty_high: string } | null =>
  FAN_QTY_UNSET_MFGS.has(Number(mfg))
    ? { qty_wide: FAN_QTY_UNSET, qty_high: FAN_QTY_UNSET }
    : null;

/**
 * The array a RATED ROW is showing, when its Qty Wide x High column offered a
 * list to pick from — `wide_high_options`, which the backend attaches both to a
 * fan-out's rows and to the equivalents of an explicitly rated array
 * (`qty_expansion.py`). Null when the column had no list, i.e. nothing on the
 * row was ever a choice and `state` is the only place an array can come from.
 */
export const ratedRowArray = (
  row: Record<string, any> | null | undefined
): { wide: number; high: number } | null => {
  if (!((row?.wide_high_options as unknown[] | undefined)?.length)) return null;
  const wide = Number(row!.qty_wide);
  const high = Number(row!.qty_high);
  if (!Number.isFinite(wide) || !Number.isFinite(high) || wide < 1 || high < 1) return null;
  return { wide, high };
};

/** What counts as "the same fan" across two ratings of the same duty — the
 *  mirror of `qty_expansion.model_identity`, and only fields that describe the
 *  PHYSICAL fan: everything the vendor computes (speed, bhp, the curves) differs
 *  per array by design, which is the reason each array keeps its own payload. */
const ratedRowIdentity = (row: Record<string, any>): string =>
  ['model', 'spn', 'wheel_dia_nominal'].map((f) => String(row?.[f] ?? '')).join('\u0000');

/** Grid order — wide ascending, then high — which is the order the backend
 *  attaches options in, before the fit-first pass reorders them for display. */
const byGridOrder = (a: Record<string, any>, b: Record<string, any>): number =>
  (Number(a.qty_wide) - Number(b.qty_wide)) || (Number(a.qty_high) - Number(b.qty_high));

/**
 * ★ Fold one more fan count's rating into the rated table (Anna, 2026-10-19).
 *
 * A `--` rating on a vendor whose backend runs its calls one at a time cannot
 * cover the grid inside one request — Twin City Fan is ~10s a configuration and
 * a 5x5 grid is 14 of them — so the counts the budget could not reach come back
 * as `wide_high_pending` and the selector asks the fan-out for them again,
 * a whole remainder per request (`rateRestOfGrid`). Each response lands here.
 *
 * ⚠ Into a LOCAL, not into the rendered table: nothing a `--` rating produces
 * reaches the screen until every round trip has answered, so that the models
 * appear together rather than part by part (`FanSelector.tsx::commit`). The
 * caller orders the finished table itself — this one only unions.
 *
 * A fan already in the list gains the new arrays in its Wide x High options and
 * their payloads; a fan only that count found is appended. The row's own
 * displayed payload is untouched, so a model keeps the numbers of the smallest
 * array it was found at, which is the array its select defaults to.
 */
export const mergeRatedRowsByModel = (
  rows: Array<Record<string, any>>,
  incoming: Array<Record<string, any>>,
  /** `orderWideHighOptions` — a merged row's list has to be put back in
   *  fit-first order, not left in the plain grid order the union produces, or
   *  an array that needs more airway than the section has can sit above one
   *  that fits. Passed in because it lives with the control that renders it. */
  orderOptions?: (
    options: Array<Record<string, any>>,
    variantRows: Record<string, Record<string, any>>,
    row: Record<string, any>
  ) => Array<Record<string, any>>
): Array<Record<string, any>> => {
  const byIdentity = new Map<string, number>();
  rows.forEach((row, index) => byIdentity.set(ratedRowIdentity(row), index));
  const merged = [...rows];

  for (const row of incoming) {
    const at = byIdentity.get(ratedRowIdentity(row));
    if (at == null) {
      byIdentity.set(ratedRowIdentity(row), merged.length);
      merged.push(row);
      continue;
    }
    const existing = merged[at];
    const seen = new Set<string>();
    const options = [
      ...((existing.wide_high_options ?? []) as Array<Record<string, any>>),
      ...((row.wide_high_options ?? []) as Array<Record<string, any>>),
    ]
      .filter((o) => (seen.has(String(o.value)) ? false : seen.add(String(o.value))))
      .sort(byGridOrder);
    const whRows = { ...(existing.__wh_rows ?? {}), ...(row.__wh_rows ?? {}) };
    merged[at] = {
      ...existing,
      wide_high_options: orderOptions ? orderOptions(options, whRows, existing) : options,
      __wh_rows: whRows,
    };
  }
  return merged;
};

/**
 * ★ Re-rank the RPI column over the WHOLE table (Anna, 2026-10-19).
 *
 * The server ranks one response as one batch (`entry.py::compute_fan_rpis`):
 * index = this fan's cost / the cheapest fan's cost, blank when there are fewer
 * than two candidates or any candidate has no CMS cost. A `--` rating that the
 * selector finishes off arrives as SEVERAL batches — one per round trip the
 * grid took — so left alone the column would mix indices worked out against
 * different cheapest fans: several rows reading 1.00 at different costs, and a
 * row's index changing when it is switched to an array that came in later.
 *
 * So once the merges are done the whole table is ranked here, by the same rules
 * — over the same entries the server ranks: every visible row AND every
 * per-array payload. That matters for Greenheck, whose basis is the eCAPS price
 * from the rating's own sales bill: a model's payloads at 2x2 and 1x4 share
 * one price (one fan count, one vendor call), but the ones merged in from
 * 1x2 carry that count's price instead. On the CMS cost basis every payload of
 * a model carries its one cost and they come out identical, which is the
 * property the server states (one batch keeps a model's index the same
 * whichever array is showing).
 *
 * ⚠ Mirrors `entry.py::compute_fan_rpis`. The two must change together.
 */
export const recomputeRatedRpis = (
  rows: Array<Record<string, any>>,
  mfgId: number | string | null | undefined
): void => {
  const basisOf = (row: Record<string, any>, key: string) => toNum(row?.[key]);
  const variantsOf = (row: Record<string, any>) =>
    Object.values((row.__wh_rows ?? {}) as Record<string, any>);
  // Ranked over rows and payloads alike, as `enriched` does on the server.
  const entries = rows.flatMap((r) => [r, ...variantsOf(r)]);

  // Only the row. The cached fan curve is a copy of it and carries a stale rpi,
  // but nothing reads that any more: `doSelect` takes the rpi from the live row
  // and the rest of the payload from the curve. Stamping the curve as well
  // would be editing the `chosenCurve` state object in place for no reader.
  const stamp = (row: Record<string, any>, rpi: string | null) => { row.rpi = rpi; };
  const blank = () => entries.forEach((e) => stamp(e, null));

  // 1. fewer than 2 CANDIDATES — the visible rows, not the payloads — because
  // there is no peer to compare against. 2. any entry missing a CMS cost
  // disqualifies the batch.
  if (rows.length < 2 || entries.some((e) => basisOf(e, 'cost_product') == null)) return blank();

  // 3. Greenheck only: when every entry came back with an eCAPS price, that is
  // the basis; otherwise the batch falls back to the CMS cost.
  const useApi = Number(mfgId) === 47
    && entries.every((e) => basisOf(e, 'api_cost_product') != null);
  const key = useApi ? 'api_cost_product' : 'cost_product';

  // 4. an entry with basis 0 is excluded from the minimum and gets no index,
  // but does not disqualify the others.
  const costs = entries.map((e) => basisOf(e, key)).filter((c): c is number => c != null && c > 0);
  if (!costs.length) return blank();
  const min = Math.min(...costs);

  for (const entry of entries) {
    const cost = basisOf(entry, key);
    stamp(entry, cost != null && cost > 0 ? formatRpi(cost / min) : null);
  }
};

/**
 * `'%.2f' % ratio`, as the server formats it (`compute_fan_rpis`).
 *
 * Python rounds a tie to the EVEN hundredth where `toFixed` takes the larger
 * one, so a fan costing exactly 1.125x the cheapest is `1.12` from the server
 * and would be `1.13` here — and costs are round numbers, so exact ties are
 * reachable rather than theoretical. Only a TRUE tie differs: a value whose
 * decimal expansion stops at a 5 in the third place. `toFixed(20)` shows that
 * expansion, so the rest of the time this is plain `toFixed(2)`.
 */
const formatRpi = (ratio: number): string => {
  if (!/^\d+\.\d{2}50*$/.test(ratio.toFixed(20))) return ratio.toFixed(2);
  const hundredths = Math.floor(ratio * 100);
  return ((hundredths % 2 === 0 ? hundredths : hundredths + 1) / 100).toFixed(2);
};

/**
 * ★ Qty Wide x High is ONE select over the combinations (Anna, 2026-10-08),
 * where dev and the port had two independent selects. This is the combined
 * select's field name, and it is UI-ONLY: the pair is still held, rated,
 * persisted and printed as the two `qty_wide` / `qty_high` fields, so
 * `onSearchField` unpacks the value into them and nothing downstream — the rate
 * envelope, the AHUFan PATCH, the print sheet, the vendor handlers — sees this
 * name at all. The available combinations are unchanged: still the same
 * 1..max_qty_wide x 1..max_qty_high grid from the fan-type mfg settings that
 * fed the two selects (updateQty, fan.selector.js:3837-3849).
 */
export const FAN_QTY_PAIR_FIELD = 'qty_wide_high';

/** The combined select's `<option>` value for a Wide/High pair — `2x3` — or
 * FAN_QTY_UNSET when either half is unset, i.e. the `--` option
 * (FAN_QTY_UNSET_MFGS). Both halves move together through this select, so the
 * half-unset case only arises from stored data. */
export const fanQtyPairValue = (wide: unknown, high: unknown): string =>
  fanQtyIsUnset(wide) || fanQtyIsUnset(high)
    ? FAN_QTY_UNSET
    : `${Number(wide)}x${Number(high)}`;

/** The inverse: the two state fields behind a combined-select value. Anything
 * that is not a `<wide>x<high>` pair — the `--` option's empty string included —
 * unsets both, which is exactly what picking `--` in the two selects did. */
export const fanQtyPairParse = (raw: unknown): { qty_wide: string; qty_high: string } => {
  const m = /^(\d+)x(\d+)$/.exec(String(raw ?? '').trim());
  return m
    ? { qty_wide: m[1], qty_high: m[2] }
    : { qty_wide: FAN_QTY_UNSET, qty_high: FAN_QTY_UNSET };
};

/**
 * `setDefaults()` — fan.selector.js:3185-3402, TRANSCRIBED VERBATIM per
 * manufacturer (every member, including the explicit `null`s, which matter: they
 * CLEAR a value the previous manufacturer left behind).
 *
 * ALL EIGHT manufacturer branches are ported (164 rosenberg, 47 greenheck,
 * 64 tcf, 221 lau, 37 ebm, 67 ZA, 23 comefri, 209 nyb) — the full legacy set.
 * A manufacturer with no branch (e.g. 152 BasX) presets nothing, exactly as
 * legacy's if/else-if chain does. Commented-out legacy members (the isolator
 * block under 47/64/221) are deliberately NOT included.
 */
export const FAN_SET_DEFAULTS: Record<number, Record<string, unknown>> = {
  // fan.selector.js:3185-3214 — rosenberg. (`wheel_mtl_id: null` appears twice
  // in the legacy literal — :3189 and :3210 — same value, so one entry here.)
  164: {
    series_id: 844,
    type_id: 2,
    wheel_width_percent_id: null,
    wheel_mtl_id: null,
    motor_speed_nominal_id: null,
    arr_id: null,
    amca_class_id: null,
    motor_mfg_id: 164,
    motor_series_id: 838,
    motor_type_id: 2,
    motor_efficiency_id: null,
    motor_power: null,
    motor_enclosure_id: null,
    motor_housing_id: null,
    motor_shaft_grounding_id: null,
    motor_position_id: 3,
    mtr_controller_id: 3,
    inlet_guard: null,
    outlet_guard: null,
    protective_cage: null,
    fan_afs_sensor: null,
    fan_afs_transmitter: null,
    style_id: null,
    mounting_id: 1,
    wheel_dia_nominal_id: null,
  },
  // fan.selector.js:3215-3240 — greenheck.
  47: {
    series_id: 491,
    wheel_width_percent_id: null,
    wheel_mtl_id: null,
    amca_class_id: null,
    motor_mfg_id: 133,
    motor_series_id: 116,
    motor_power: null,
    motor_speed_nominal_id: null,
    motor_efficiency_id: 1,
    motor_enclosure_id: null,
    motor_position_id: 3,
    motor_shaft_grounding_id: null,
    mtr_supplied_by_id: 2,
    mtr_controller_id: 1,
    mounting_id: 1,
    wheel_dia_nominal_id: null,
  },
  // fan.selector.js:3241-3276 — tcf. `series_id: 200` is EPFN; the cascade
  // falls back to 202 (EPQN) when 200 is not in the loaded list (the series
  // default_values order). (`motor_shaft_grounding_id: null` appears twice in
  // the legacy literal — :3254 and :3272.)
  64: {
    series_id: 200,
    type_id: 1,
    arr_id: 1,
    wheel_width_percent_id: null,
    amca_class_id: null,
    motor_mfg_id: 133,
    motor_series_id: 116,
    motor_efficiency_id: 1,
    motor_enclosure_id: null,
    motor_housing_id: null,
    motor_shaft_grounding_id: null,
    mtr_supplied_by_id: 1,
    mtr_controller_id: 1,
    inlet_guard: null,
    outlet_guard: null,
    protective_cage: null,
    fan_afs_sensor: null,
    fan_afs_transmitter: null,
    wheel_mtl_id: null,
    housed_discharge_id: null,
    rotation_id: 1,
    orientation_id: 1,
    mounting_id: 1,
    motor_power: null,
    wheel_dia_nominal_id: null,
  },
  // fan.selector.js:3277-3309 — lau.
  221: {
    series_id: 1302,
    wheel_width_percent_id: null,
    amca_class_id: null,
    motor_mfg_id: 133,
    motor_series_id: 116,
    motor_efficiency_id: 1,
    motor_enclosure_id: null,
    motor_housing_id: null,
    mtr_supplied_by_id: 1,
    mtr_controller_id: 1,
    inlet_guard: null,
    outlet_guard: null,
    protective_cage: null,
    fan_afs_sensor: null,
    fan_afs_transmitter: null,
    wheel_mtl_id: null,
    housed_discharge_id: null,
    rotation_id: 1,
    orientation_id: 1,
    motor_shaft_grounding_id: null,
    mounting_id: 1,
    motor_power: null,
    wheel_dia_nominal_id: null,
  },
  // fan.selector.js:3310-3334 — ebm. (`amca_class_id: null` appears twice —
  // :3327 and :3332.)
  37: {
    wheel_mtl_id: null,
    type_id: 2,
    mtr_controller_id: 3,
    motor_shaft_grounding_id: null,
    motor_efficiency_id: null,
    motor_enclosure_id: null,
    motor_position_id: 3,
    motor_mfg_id: 37,
    motor_series_id: 836,
    motor_type_id: 2,
    motor_housing_id: 3,
    series_id: 708,
    motor_power: null,
    motor_speed_nominal_id: null,
    amca_class_id: null,
    housed_discharge_id: null,
    style_id: null,
    mounting_id: 1,
    orientation_id: 1,
    wheel_dia_nominal_id: null,
  },
  // fan.selector.js:3335-3359 — ziehl-abegg. (`amca_class_id: null` twice.)
  67: {
    wheel_mtl_id: null,
    type_id: 2,
    mtr_controller_id: 3,
    motor_shaft_grounding_id: null,
    motor_efficiency_id: null,
    motor_enclosure_id: null,
    motor_position_id: 3,
    motor_mfg_id: 67,
    motor_series_id: 1288,
    motor_type_id: 2,
    motor_housing_id: 3,
    series_id: 1,
    motor_power: null,
    motor_speed_nominal_id: null,
    amca_class_id: null,
    housed_discharge_id: null,
    style_id: null,
    mounting_id: 1,
    orientation_id: 1,
    wheel_dia_nominal_id: null,
  },
  // fan.selector.js:3360-3383 — comefri.
  23: {
    wheel_mtl_id: null,
    type_id: 1,
    mtr_controller_id: 3,
    motor_efficiency_id: 1,
    motor_enclosure_id: null,
    motor_position_id: null,
    motor_mfg_id: 133,
    motor_series_id: 116,
    motor_type_id: 1,
    motor_housing_id: null,
    series_id: 173,
    motor_speed_nominal_id: null,
    amca_class_id: null,
    housed_discharge_id: null,
    discharge_id: 2,
    motor_power: null,
    mounting_id: 1,
    orientation_id: 1,
    wheel_dia_nominal_id: null,
    motor_shaft_grounding_id: null,
  },
  // fan.selector.js:3384-3401 — nyb (the header popup's DEFAULT manufacturer,
  // fan.selector.js:1421 `manufacturer.find(e => e.id == 209)`).
  209: {
    type_id: 1,
    style_id: null,
    series_id: 1391,
    wheel_mtl_id: null,
    amca_class_id: null,
    rotation_id: 1,
    arr_id: 1,
    motor_mfg_id: 133,
    motor_series_id: 116,
    motor_efficiency_id: 1,
    motor_enclosure_id: null,
    motor_housing_id: null,
    wheel_dia_nominal_id: null,
    motor_shaft_grounding_id: null,
  },
  // Dev-sync H — annexair (fan.selector.js setDefaults, :3453-3475 on merged
  // dev). The NYB literal + amca_class_id:null + the two Annexair inlet-temp
  // presets (DB 68 / WB 60). Switching AWAY from 237 leaves 68/60 behind —
  // no other branch resets fan_inlet_temp/_wb — reproduced, not fixed.
  // Sync L (Annexair fan fixes, commit 77263bea90): the WB preset moved 55 → 60
  // to match Anna's setDefaults change (the render self-heal below defaults to
  // the same 60 when mfg lands on 237 without a change event).
  // type_id / series_id are left unset on purpose: the NYB literals (Plenum 1,
  // series 1391) don't exist for Annexair, which only offers Plug (type 2).
  // Preset 1 survived the un-armed open cascade as a phantom "1" option and
  // emptied every child list (they filter by type_id=1). Null lets the
  // sole-option rule adopt the real Plug type and series.
  237: {
    type_id: null,
    style_id: null,
    series_id: null,
    wheel_mtl_id: null,
    amca_class_id: null,
    rotation_id: 1,
    arr_id: 1,
    motor_mfg_id: null, // Annexair motors are Domel (236), not Baldor (133)
    motor_series_id: null, // 116 (Super-E) isn't offered for Annexair; the 'AZ' fallback fills it
    motor_efficiency_id: 1,
    motor_enclosure_id: null,
    motor_housing_id: null,
    wheel_dia_nominal_id: null,
    fan_inlet_temp: 68,
    fan_inlet_temp_wb: 60,
    motor_shaft_grounding_id: null,
    // (Qty Wide x High used to be forced to `--` here, for Annexair only. Now
    // that every manufacturer opens on `--` it is `fanQtyDefaultsFor`, applied
    // on the same mfg-change event for whichever vendor was picked.)
  },
};

/**
 * `mergeIdsWithCMS`'s per-manufacturer state overrides — fan.selector.js
 * :2182-2220. These are UNCONDITIONAL sets (not null-gated) re-applied on every
 * select re-render, so the port applies them on each cascade settle.
 *
 * The legacy expressions are `(self.state.get('fan_model') && self.state.get(X))
 * || <literal>` — i.e. the literal wins unless a fan MODEL has already been
 * chosen. Passed the live state so that guard is reproduced verbatim.
 */
export function fanMergeMfgOverrides(
  s: Record<string, unknown>
): Record<string, unknown> {
  const mfg = Number(s.mfg_id);
  const keep = (field: string, dflt: number) =>
    (s.fan_model && s[field]) || dflt;
  // :2182-2201 — greenheck.
  if (mfg == 47) {
    return {
      mtr_supplied_by_id: keep('mtr_supplied_by_id', 2),
      fan_isolators_supplied_by_id: keep('fan_isolators_supplied_by_id', 2),
      mtr_controller_id: keep('mtr_controller_id', 1),
      motor_position_id: keep('motor_position_id', 3),
    };
  }
  // :2202-2211 — tcf + lau.
  if (mfg == 64 || mfg == 221) {
    return {
      mtr_controller_id: 1,
      mtr_supplied_by_id: 1,
      fan_isolators_supplied_by_id: 1,
    };
  }
  // :2212-2214 — rosenberg.
  if (mfg == 164) return { motor_position_id: 1, mtr_controller_id: 3 };
  // :2215-2220 — ebm + ziehl-abegg.
  if (mfg == 37 || mfg == 67) return { mtr_controller_id: 3 };
  return {};
}

/**
 * The fields the `renderSelects` UNGATED sole-option auto-select applies to
 * (fan.selector.js:7644-7654 — every re-rendered cascade select except
 * `wheel_width_percent_id`, which legacy force-clears at :7636-7643).
 *
 * ★ `wheel_width_percent_id` is a DELIBERATE deviation from legacy, asked for
 * 2026-10-03: when a manufacturer/series offers exactly ONE wheel width (every
 * Annexair series, and most vendors, offer only 100%), leaving the select on
 * '--' makes the user pick the only value there is. So the sole-option rule
 * applies to it like every other cascade select. Everything else about the
 * field is unchanged: with two or more widths it still opens unset, and
 * `onSearchField` still force-clears it on a Manufacturer/Series change (the
 * PR #773 carry-over fix) — the reload simply re-fills it when the new vendor
 * has a single width.
 *
 * ★ `wheel_dia_percent_id` is EXCLUDED on top of legacy's own exclusion: legacy
 * DOES put 100 in its state, but its `collectData` sweeps the DOM (:4248) and
 * the header popup renders NO wheel_dia_percent select (confirmed live: the
 * field is absent from the legacy popup's field census, present in ours), so the
 * legacy rate envelope never carries `wheel_dia_percent` for 209/64. Our
 * envelope is STATE-driven, so filling it would add a member legacy omits.
 * (For the vendor mfgs 164/47/23/37/67 it is a REQUIRED field and the dependency
 * engine already fills it in both hosts — unchanged.)
 */
export const FAN_SOLE_OPTION_EXCLUDE = new Set<string>([
  'mfg_id',
  'wheel_dia_percent_id',
  'motor_controller',
]);

/**
 * `reset` (fan.selector.js:8219-8298) — the literal the Reset-to-defaults
 * button writes over `{...self.state.toJSON()}`. Transcribed key-for-key and in
 * legacy's order, because the shape of this list is the whole behaviour: what
 * it does NOT name (airflow, TSP, elevation, the entering conditions, the
 * assembly clearances) is what dev deliberately KEEPS when you reset.
 *
 * ★ `fan_isolators_supplied_by_id` is in dev's literal TWICE — `1` at :8264 and
 * `null` at :8291. JS keeps the last, so dev resets it to null and the `1` is
 * dead. Mirrored, not "corrected"; the same call as the `undefined__fan_
 * selection` print title.
 *
 * Keys dev nulls that this port carries under a different name are grouped at
 * the end. Our state is FLAT — it holds the select's own name where legacy held
 * a blob member or an aliased column — so nulling only dev's spelling would
 * leave the control still populated. Each pair is the alias the seed builds
 * (FanSelector.tsx guardMap / si_mounting_method_id / si_specified_deflection_id).
 */
export const FAN_RESET_OVERRIDES: Record<string, unknown> = {
  master_fan_id: null,
  master_motor_id: null,
  orientation_id: 1,
  qty_wide: 1,
  qty_high: 1,
  mtr_controller_id: 1,
  min_aw_width: null,
  min_aw_height: null,
  si_mounting_method_id: null,
  si_specified_deflection_id: null,
  master_fan_sheave_id: null,
  ams_casing_mount_id: null,
  master_belt_id: null,
  master_motor_sheave_id: null,
  master_motor_base_id: null,
  motor_position_id: null,
  motor_qty: 1,
  fan_qty: 1,
  motor_voltage_id: 1,
  total_efficiency: null,
  pricing_method_id: 1,
  cost_product_user: null,
  cost_freight_user: null,
  net_price_product_user: null,
  net_price_freight_user: null,
  motor_pricing_method_id: 1,
  motor_cost_product_user: null,
  motor_cost_freight_user: null,
  motor_net_price_product_user: null,
  motor_net_price_freight_user: null,
  fan_model: null,
  redundancy_id: 1,
  motor_model: null,
  fan_mfg: null,
  curve: null,
  mounting_id: 1,
  mtr_base_supplied_by_id: 1,
  mtr_supplied_by_id: 1,
  in_guard_id: null,
  out_guard_id: null,
  protect_cage_id: null,
  afs_sensor_id: null,
  afs_xmitter_id: null,
  isolator_cost_registry_key: null,
  motor_base_cost_registry_key: null,
  isolators: null,
  inlet_guard_id: null,
  outlet_guard_id: null,
  protective_cage_id: null,
  fan_afs_sensor_id: null,
  fan_afs_transmitter_id: null,
  mounting_method_id: null,
  fan_spacing_ud_sides: null,
  fan_spacing_ud_top_btm: null,
  fan_spacing_sides_id: 1,
  fan_spacing_top_btm_id: 1,
  mounting_plane_id: null,
  casing_mount_id: null,
  wheel_mtl_id: null,
  wheel_width_percent_id: null,
  rotation_id: null,
  isolator_mfg_id: null,
  fan_isolators_supplied_by_id: null, // ← dev's duplicate key; null is the winner
  isolator_type_id: null,
  isolator_series_id: null,
  specified_deflection: null,
  assy_height_from_floor: null,
  wheel_type_id: null,
  motor_speed_nominal_id: null,
  // --- our aliases for the above (see the note in this comment block) ---
  inlet_guard: null,
  outlet_guard: null,
  protective_cage: null,
  fan_afs_sensor: null,
  fan_afs_transmitter: null,
  redundancy: 1, // the seed's echo of redundancy_id (FanSelector.tsx :1370)
};

/**
 * The fan_params members that have NO model column, so dev's
 * `fan_params: JSON.stringify({mfg_id})` is the only thing that clears them.
 * Everything else in ALLOWED_FAN_PARAMS is also in SEED_MODEL_FIELDS and comes
 * BACK from the column when `reset` re-runs `initialize` (:8304) — which is why
 * dev keeps your airflow and static pressure across a reset and only drops the
 * search filters. Derived rather than hand-listed would be nicer, but
 * SEED_MODEL_FIELDS is the authority and lives in FanSelector.tsx; the caller
 * intersects the two.
 */
export const FAN_RESET_BLOB_ONLY_MOTOR_KEYS = [
  'motor_mfg_id',
  'motor_series_id',
  'motor_enclosure_id',
  'motor_efficiency_id',
  'motor_housing_id',
  'motor_shaft_grounding_id',
] as const;

/** motor_base_params → `mtr_base_*` (FanSelector.tsx seed). `motor_base_params:
 *  null` clears exactly these three; `mtr_base_supplied_by_id` is a COLUMN and
 *  is reset to 1 by the literal above, not cleared here. */
export const FAN_RESET_MOTOR_BASE_KEYS = [
  'mtr_base_mfg_id',
  'mtr_base_series_id',
  'mtr_base_mtl_id',
] as const;

/** The five isolation fields `mergeIdsWithCMS` seeds from `isolation_defaults`
 * (fan.selector.js:2222-2239). Order = the legacy `if` chain's key order. */
export const FAN_ISOLATION_DEFAULT_FIELDS = [
  'isolator_type_id',
  'isolator_mfg_id',
  'isolator_series_id',
  'specified_deflection',
  'fan_isolators_supplied_by_id',
] as const;

/**
 * `DEFAULT_FIELDS` — fan.selector.js:178-396, transcribed verbatim per
 * manufacturer (64 TCF, 209 NYB, 237 annexair, 221 Lau, 152 BasX,
 * 164 rosenberg, 47 greenheck, 37 ebm, 67 ziehl-abegg, 23 comefri).
 *
 * This is the gate legacy's dependency handler consults before it applies a
 * field's `default_values` pick to a field whose value is currently NULL
 * (`setDefaultsIfNullChecker`, js:1305-1314):
 *
 *     return isNull(self.state.get("mfg_id")) ||
 *            DEFAULT_FIELDS[self.state.get("mfg_id")].has(parameter);
 *
 * It is a DIFFERENT map from `REQUIRED_FIELDS` (js:398-521 → FAN_REQUIRED_FIELDS
 * below), which gates the #rate button. The port previously had no
 * `DEFAULT_FIELDS` at all and reused `FAN_REQUIRED_FIELDS` for both jobs — but
 * the required sets are tiny and almost entirely NUMBER inputs (`FAN_REQ_TCF` is
 * just airflow_actual / airflow_per_fan / tsp), so on TCF, BasX, Lau and NYB no
 * SELECT could ever take its default and every dependent select came up blank
 * where dev pre-fills it. Same shape, smaller blast radius, on the other five.
 *
 * The commented-out members are legacy's own (`wheel_width_percent_id` on
 * 64/221/152/164/47/37, `max_peak_pressure_percent` on 152/164) and are
 * deliberately kept OUT of the Sets — see the wheel-width note in
 * FAN_SET_DEFAULTS: those fields must stay unset across a mfg change.
 */
const FAN_DEFAULT_FIELDS_TCF = new Set<string>([
  'mfg_id', 'type_id', 'series_id', 'arr_id', 'qty_wide', 'qty_high',
  'wheel_dia_percent_id',
  // 'wheel_width_percent_id',
  'wheel_mtl_id', 'mtr_controller_id', 'motor_voltage_id', 'airflow_actual',
  'airflow_per_fan', 'tsp', 'max_peak_pressure_percent', 'fan_inlet_pressure',
  'fan_inlet_temp', 'fan_inlet_vane_id', 'fan_disch_diffuser_id', 'damper_id',
  'motor_type_id', 'cube_housing_id', 'style_id', 'redundancy_id',
  'speed_condition_id', 'housed_discharge_id', 'motor_position_id',
]);
export const FAN_DEFAULT_FIELDS: Record<number, Set<string>> = {
  // js:179-207 — TCF. js:236-265 — Lau, member-for-member identical.
  64: FAN_DEFAULT_FIELDS_TCF,
  221: FAN_DEFAULT_FIELDS_TCF,
  // js:208-219 — nyb.
  209: new Set<string>([
    'mfg_id', 'type_id', 'series_id', 'style_id', 'wheel_mtl_id', 'rotation_id',
    'arr_id', 'airflow_per_fan', 'airflow_actual', 'tsp',
  ]),
  // js:220-235 — annexair (the nyb set + inlet pressure/DB/WB + mtr_safety).
  237: new Set<string>([
    'mfg_id', 'type_id', 'series_id', 'style_id', 'wheel_mtl_id', 'rotation_id',
    'arr_id', 'airflow_per_fan', 'airflow_actual', 'tsp', 'fan_inlet_pressure',
    'fan_inlet_temp', 'fan_inlet_temp_wb', 'mtr_safety',
  ]),
  // js:266-295 — BasX. The TCF set minus motor_voltage_id, style_id kept;
  // `max_peak_pressure_percent` is commented out in legacy (js:275).
  152: new Set<string>([
    'mfg_id', 'type_id', 'series_id', 'arr_id', 'qty_wide', 'qty_high',
    'wheel_dia_percent_id',
    // 'wheel_width_percent_id',
    'wheel_mtl_id', 'mtr_controller_id', 'airflow_actual', 'airflow_per_fan',
    'tsp',
    /* 'max_peak_pressure_percent', */
    'fan_inlet_pressure', 'fan_inlet_temp', 'fan_inlet_vane_id',
    'fan_disch_diffuser_id', 'damper_id', 'motor_type_id', 'cube_housing_id',
    'style_id', 'redundancy_id', 'speed_condition_id', 'housed_discharge_id',
    'motor_position_id',
  ]),
  // js:296-325 — rosenberg. style_id/redundancy_id move up next to series_id
  // and `max_peak_pressure_percent` is commented out (js:305).
  164: new Set<string>([
    'mfg_id', 'type_id', 'series_id', 'style_id', 'redundancy_id', 'arr_id',
    'qty_wide', 'qty_high', 'wheel_dia_percent_id',
    // 'wheel_width_percent_id',
    'wheel_mtl_id', 'mtr_controller_id', 'motor_voltage_id', 'airflow_actual',
    'airflow_per_fan', 'tsp',
    /* 'max_peak_pressure_percent', */
    'fan_inlet_pressure', 'fan_inlet_temp', 'fan_inlet_vane_id',
    'fan_disch_diffuser_id', 'damper_id', 'motor_type_id', 'cube_housing_id',
    'speed_condition_id', 'housed_discharge_id', 'motor_position_id',
  ]),
  // js:326-355 — greenheck. rosenberg's set WITH max_peak_pressure_percent.
  47: new Set<string>([
    'mfg_id', 'type_id', 'series_id', 'style_id', 'redundancy_id', 'arr_id',
    'qty_wide', 'qty_high', 'wheel_dia_percent_id',
    // 'wheel_width_percent_id',
    'wheel_mtl_id', 'mtr_controller_id', 'motor_voltage_id', 'airflow_actual',
    'airflow_per_fan', 'tsp', 'max_peak_pressure_percent', 'fan_inlet_pressure',
    'fan_inlet_temp', 'fan_inlet_vane_id', 'fan_disch_diffuser_id', 'damper_id',
    'motor_type_id', 'cube_housing_id', 'speed_condition_id',
    'housed_discharge_id', 'motor_position_id',
  ]),
  // js:356-370 — ebm_papst. NB: no `series_id` and no `airflow_per_fan`.
  37: new Set<string>([
    'mfg_id', 'type_id', 'arr_id', 'qty_wide', 'qty_high',
    // 'wheel_width_percent_id',
    'wheel_mtl_id', 'mtr_controller_id', 'motor_voltage_id', 'airflow_actual',
    'tsp', 'housed_discharge_id', 'motor_position_id',
  ]),
  // js:371-385 — ziehl-abegg. The ebm set (no commented wheel-width member).
  67: new Set<string>([
    'mfg_id', 'type_id', 'arr_id', 'qty_wide', 'qty_high', 'wheel_mtl_id',
    'mtr_controller_id', 'motor_voltage_id', 'airflow_actual', 'tsp',
    'housed_discharge_id', 'motor_position_id',
  ]),
  // js:386-395 — comefri. The narrowest set of the ten.
  23: new Set<string>([
    'mfg_id', 'type_id', 'arr_id', 'airflow_actual', 'tsp', 'motor_position_id',
    'motor_shaft_grounding_id', 'mtr_supplied_by_id',
  ]),
};

/**
 * fan.selector.js:398-521 — per-mfg REQUIRED_FIELDS (Sets), the #rate gate's
 * input (asterisks + checkRequiredFields). Transcribed VERBATIM per mfg.
 *
 * ★ These are NOT two shared sets. The port aliased them (`FAN_REQ_TCF` for
 * 64/152/221/209, `FAN_REQ_VENDOR` for 164/47/23/37/67) and three mfgs came out
 * wrong against dev:
 *   - 221 (Lau, :416-421) also requires `mtr_controller_id`
 *   - 152 (BasX, :423-428) is a set of its OWN — wheel_dia_percent_id /
 *     airflow_actual / fan_inlet_temp — it requires NEITHER airflow_per_fan nor
 *     tsp, both of which the alias imposed
 *   - 67 (Ziehl-Abegg, :493-504) also requires `motor_voltage_id`
 * Keep them literal; a "these two look identical" merge is how the drift began.
 */
export const FAN_REQUIRED_FIELDS: Record<number, Set<string>> = {
  // :400-415 — TCF.
  64: new Set<string>(['airflow_actual', 'airflow_per_fan', 'tsp']),
  // :416-421 — Lau.
  221: new Set<string>(['mtr_controller_id', 'airflow_actual', 'airflow_per_fan', 'tsp']),
  // :423-428 — BasX.
  152: new Set<string>(['wheel_dia_percent_id', 'airflow_actual', 'fan_inlet_temp']),
  // :429-440 — rosenberg.
  164: new Set<string>([
    'series_id', 'wheel_dia_percent_id', 'airflow_actual', 'airflow_per_fan',
    'tsp', 'fan_inlet_pressure', 'fan_inlet_temp',
  ]),
  // :441-457 — greenheck.
  47: new Set<string>([
    'series_id', 'wheel_dia_percent_id', 'airflow_actual', 'airflow_per_fan',
    'tsp', 'fan_inlet_pressure', 'fan_inlet_temp',
  ]),
  // :458-475 — comefri.
  23: new Set<string>([
    'series_id', 'wheel_dia_percent_id', 'airflow_actual', 'airflow_per_fan',
    'tsp', 'fan_inlet_pressure', 'fan_inlet_temp',
  ]),
  // :476-492 — ebm.
  37: new Set<string>([
    'series_id', 'wheel_dia_percent_id', 'airflow_actual', 'airflow_per_fan',
    'tsp', 'fan_inlet_pressure', 'fan_inlet_temp',
  ]),
  // :493-504 — ziehl-abegg. NOTE `motor_voltage_id`: no other vendor set has it.
  67: new Set<string>([
    'series_id', 'wheel_dia_percent_id', 'motor_voltage_id', 'airflow_actual',
    'airflow_per_fan', 'tsp', 'fan_inlet_pressure', 'fan_inlet_temp',
  ]),
  // :505-511 — nyb.
  209: new Set<string>(['airflow_actual', 'airflow_per_fan', 'tsp']),
  // Dev-sync H — annexair (:512-521 on merged dev): the TCF trio + inlet DB/WB
  // temps + motor safety %.
  237: new Set<string>([
    'airflow_actual', 'airflow_per_fan', 'tsp', 'fan_inlet_temp',
    'fan_inlet_temp_wb', 'mtr_safety',
  ]),
};

/**
 * ★ REQUIRED-BUT-UNENFORCEABLE — listed in `REQUIRED_FIELDS`, but the legacy
 * template renders no control for it, so dev can never block on it.
 *
 * `checkAndUpdateRequiredFields` (js:7728-7752) does NOT iterate the required
 * Set. It iterates `self.inputsDOM` — built by `getTreeOfAllInputs` (js:7659)
 * from `querySelectorAll('input[name], select[name], span[name]')` — and only
 * tests a field when the Set `.has()` that element's NAME. A required field with
 * no DOM element is never visited, so it never contributes to `valid`.
 *
 * `wheel_dia_percent_id` is exactly that: ZERO occurrences of
 * `name="wheel_dia_percent_id"` across the 2258-line `fan.selector.tmpl` (the
 * two wheel selects that DO render are `wheel_dia_nominal_id` "Wheel Diameter",
 * tmpl:129-133, and `wheel_width_percent_id` "Wheel Width %"). So it is a dead
 * requirement on dev for 152/164/47/23/37/67.
 *
 * Our gate is state-driven, not DOM-driven, so it had no equivalent of legacy's
 * accidental skip and DID enforce the field — and nothing in the port ever fills
 * it. The params call returns exactly one option (`MasterAHUFanWheelDiaPercent`
 * id 100, value 100), but `wheel_dia_percent_id` sits in FAN_SOLE_OPTION_EXCLUDE
 * so the sole-option auto-select skips it, and no `DEFAULT_FIELDS` set names it
 * either. Permanently null + enforced = #rate permanently greyed on all five
 * vendor manufacturers — the Ziehl-Abegg report this set was corrected for.
 *
 * Skipping it here (rather than letting the sole option fill it) is the parity
 * fix: legacy's `collectData` also sweeps the DOM (:4248), so dev's rate
 * envelope carries no `wheel_dia_percent` member. Filling the state would add
 * one dev never sends.
 */
export const FAN_REQUIRED_FIELDS_WITHOUT_INPUT = new Set<string>([
  'wheel_dia_percent_id',
]);

/**
 * Fields that are in a manufacturer's `FAN_REQUIRED_FIELDS` set but draw NO
 * asterisk on their label — legacy's `REQUIRED_FIELDS_WITHOUT_ASTERISK`
 * (fan.selector.js:7723). They are still enforced by the #rate gate; only the
 * `*` is suppressed.
 *
 * `airflow_per_fan` was added 2026-10-08 at Anna's request: on the Performance
 * tab it is paired with "total" in one row ("Airflow per fan | total") and the
 * user fills either one, since `calcAirflow` derives the other from Qty Wide x
 * High. Marking the per-fan half required read as if both had to be typed, and
 * on the vendors that allow Qty Wide x High `--` the per-fan figure is
 * deliberately blank and not enforced at all (see the gate in FanSelector).
 */
export const FAN_REQUIRED_FIELDS_WITHOUT_ASTERISK = new Set<string>([
  'mtr_safety',
  'airflow_per_fan',
]);

/**
 * fan.selector.js:7694-7696 `specialFilledCheckeds` — extra per-field predicates
 * ANDed on top of the null/''/None test. Only `tsp` has one: a total static
 * pressure of 0 counts as NOT filled and keeps #rate greyed.
 */
export const FAN_REQUIRED_SPECIAL_CHECKS: Record<string, (v: unknown) => boolean> = {
  tsp: (v) => Number(v) !== 0,
};

/**
 * fan.selector.js:7700-7709 `checkIfIsFilled`, verbatim. Legacy reads the DOM
 * element's `.value` (always a string), so the rejected values are null, '',
 * 'None' and 'none' — NOT the number 0 (except via `tsp`'s special check), and
 * NOT '--' (a blank select's value is `""`, tmpl:149). Our state holds
 * numbers/nulls and the 'None' strings only ever arrive from a params payload,
 * but both are kept so the predicate is legacy's, not a re-derivation.
 */
export function fanFieldIsFilled(name: string, v: unknown): boolean {
  if (v == null || v === '' || v === 'None' || v === 'none') return false;
  const special = FAN_REQUIRED_SPECIAL_CHECKS[name];
  return !special || special(v);
}

/** mfgs whose chooseFan triggers a per-fan curve fetch (getFanData). */
export const CURVE_VENDOR_MFGS = new Set<number>([23, 37, 67, 164]);

/**
 * KA-5254 (dev merged 2026-07-15) — vendors whose rating request needs the
 * current fan slot's ahu_fan_id/master_motor_id and RETAINS the isolator
 * selection keys, so the backend can compute min_aw_width/min_aw_height per
 * rated candidate consistently with /ahu/fans/<id>
 * (fan.selector.js rate() — MIN_AW_MFG_IDS, :3996 on merged dev).
 * Dev-sync H: 237 (Annexair) joined the set (fan.selector.js:4137).
 */
export const FAN_MIN_AW_MFG_IDS = new Set<number>([209, 64, 23, 37, 47, 221, 164, 67, 237]);

/**
 * fan.selector.js:494-506 — fieldsShortNames: id-key → CMS short key. Used by
 * collectData to resolve the search-field `name`s (verbatim subset that the
 * envelope needs).
 */
export const FAN_FIELD_SHORT_NAMES: Record<string, string> = {
  mtr_controller_id: 'mtr_controller',
  mounting_method_id: 'spring_iso_mounting',
  inlet_guard: 'in_guard',
  fan_afs_transmitter: 'afs_xmitter',
};

// ---------------------------------------------------------------------------
// RATE-ENVELOPE REMAP (fan.selector.js collectData 4128-4256 + the tmpl
// data-name/data-value attributes — fan.selector.tmpl 80-1140/1576-1640).
//
// ★ PARITY-CRITICAL (2026-07-08 Django-shell replay evidence): the legacy
// collectData sweeps every `.search_field` and keys the envelope input by the
// element's `data-name` (type/series/arr/…, NOT the raw `*_id` state keys),
// valuing it by the selected <option>'s `data-value` — the CMS/master row's
// series/value/power CODE, jQuery-typed ("4" → 4). The backend vendor mappers
// (network_control/rating/integrated_parts/fan/<vendor>/mapping.py) translate
// exactly those name-keys; unknown keys are silently skipped, so raw *_id keys
// made TCF 400 (missing series → frequency → arr) and NYB 422 (missing
// FanSize/Arrangement/FanClass/WheelMaterialCategory/IsVSD).
//
// Four value families (mirroring where the legacy <option>s come from):
//   DOC      — fans-CMS-doc selects; data-value = power||series||value||id
//              (renderSelectsAfterDependencyChange js:7451-7460); motor_mfg
//              options carry the mfg NAME (js:7409).
//   MASTER   — master-list selects; data-value = the structural-switch code
//              (js:7366-7369 — e.g. mtr_controller 'vfd', in_guard
//              'inlet_guard_hpa', afs_xmitter .configuration).
//   ISOLATOR — spring-isolator CMS doc; MOTOR-BASE — motor-base CMS doc.
// ---------------------------------------------------------------------------

/** fans-CMS-doc search-selects: React state field → envelope `data-name` key.
 * motor_speed_nominal is re-keyed `speed_nominal` for greenheck(47)
 * (collectData js:4149-4153) — handled at collection time. */
export const FAN_RATE_DOC_FIELDS: Record<string, string> = {
  type_id: 'type',
  series_id: 'series',
  style_id: 'style',
  arr_id: 'arr',
  rotation_id: 'rotation',
  amca_class_id: 'amca_class',
  wheel_type_id: 'wheel_type',
  wheel_dia_nominal_id: 'wheel_dia_nominal',
  wheel_mtl_id: 'wheel_mtl',
  cube_housing_id: 'cube_housing',
  redundancy_id: 'redundancy',
  speed_condition_id: 'speed_condition',
  housed_discharge_id: 'housed_discharge',
  orientation_id: 'orientation',
  motor_type_id: 'motor_type',
  motor_mfg_id: 'motor_mfg',
  motor_series_id: 'mtr_series',
  motor_efficiency_id: 'efficiency',
  motor_enclosure_id: 'enclosure',
  motor_housing_id: 'mtr_housing',
  motor_speed_nominal_id: 'motor_speed_nominal',
};

/** master-list search-selects: field → envelope key. inlet_guard is re-keyed
 * `inlet_guard` (with null → '--') for ebm(37)/ZA(67) (js:4154-4163) — handled
 * at collection time. NOTE the tmpl swaps the guards' DOM id and data-name:
 * select#inlet_guard carries data-name="inlet_guard_id" (tmpl:778) — the
 * envelope key IS the `_id` name while the VALUE is the vendor code. */
export const FAN_RATE_MASTER_FIELDS: Record<string, string> = {
  mounting_id: 'mounting',
  motor_position_id: 'motor_position',
  motor_shaft_grounding_id: 'shaft_grounding',
  mtr_controller_id: 'mtr_controller_id',
  mtr_supplied_by_id: 'mtr_supplied_by',
  mtr_base_supplied_by_id: 'mtr_base_supplied_by',
  fan_isolators_supplied_by_id: 'fan_isolators_supplied_by',
  mounting_method_id: 'mounting_method',
  inlet_guard: 'inlet_guard_id',
  outlet_guard: 'outlet_guard_id',
  protective_cage: 'protective_cage_id',
  fan_afs_sensor: 'afs_sensor_id',
  fan_afs_transmitter: 'afs_xmitter_config',
};

/** spring-isolator CMS search-selects (tmpl:1576-1629): field → envelope key
 * (the data-names DROP the `_id`). Stripped server-side of the TCF-family +
 * rosenberg envelopes by the isolator/mtr key filter (buildFanRateInput). */
export const FAN_RATE_ISOLATOR_FIELDS: Record<string, string> = {
  isolator_type_id: 'isolator_type',
  isolator_mfg_id: 'isolator_mfg',
  isolator_series_id: 'isolator_series',
  specified_deflection: 'specified_deflection',
};

/** motor-base CMS search-selects (tmpl:1098-1125): data-name === the `_id`
 * name; value = option data-value. Always stripped for TCF-family/rosenberg. */
export const FAN_RATE_MOTOR_BASE_FIELDS: Record<string, string> = {
  mtr_base_mfg_id: 'mtr_base_mfg_id',
  mtr_base_series_id: 'mtr_base_series_id',
  mtr_base_mtl_id: 'mtr_base_mtl_id',
};

/** numeric search-inputs (legacy `Number(value.replace(/,/g,''))`, js:4234).
 * EMPTY fields are OMITTED — legacy yields undefined, which JSON.stringify
 * drops from the POST body. */
export const FAN_RATE_NUMERIC_FIELDS = [
  'airflow_per_fan', 'airflow_actual', 'tsp', 'isp', 'esp', 'esp_desired',
  'apd', 'max_peak_pressure_percent', 'fan_inlet_pressure', 'fan_inlet_temp',
  // Dev-sync H: the two Annexair search_field inputs (fan.selector.tmpl:509/
  // :1032). Legacy sweeps them for EVERY mfg (hidden inputs included) — a
  // value left behind by a 237 pick rides along in other vendors' envelopes,
  // where the backend mapper skips the unknown keys. Empty ⇒ omitted.
  'fan_inlet_temp_wb', 'mtr_safety',
  'qty_wide', 'qty_high', 'fan_spacing_ud_sides', 'fan_spacing_ud_top_btm',
];

/**
 * The voltage select's data-name is PER-MFG (tmpl:1026-1030 + collectData
 * js:4165-4186): 64/221 → `frequency`; 164 (and 37/67 via js:4180-4185) →
 * `motor_voltage`; 47 → `voltage_id` (valued with the raw select id);
 * everyone else (209/152/23) → `voltage_id` (valued with the option NAME).
 */
export function fanVoltageRateKey(mfgId: number): string {
  if (mfgId == 64 || mfgId == 221) return 'frequency';
  if (mfgId == 164 || mfgId == 37 || mfgId == 67) return 'motor_voltage';
  return 'voltage_id';
}

/** jQuery `.data()` numeric auto-conversion (dataAttr: `data === +data + ""`)
 * — the legacy option.data('value') reads pass through it, so "4" → 4 while
 * "4vu"/"460/3/60" stay strings. Mirror it exactly. */
export function jqDataValue(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  return v === String(+v) ? +v : v;
}

/** A dynamic <option>'s data-value for a CMS-doc row — the legacy chain
 * `power || series || value || id` (js:7451-7460), jQuery-typed. */
export function fanOptionDataValue(row: Record<string, unknown>): unknown {
  const v =
    (row.power as unknown) ||
    (row.series as unknown) ||
    (row.value as unknown) ||
    (row.id as unknown);
  return jqDataValue(v);
}

/** Motor rate-fields the backend resolves by a case-insensitive TEXT match on
 * the master `value` column, then `name`
 * (network_control/rating/integrated_parts/fan/aw_and_motor.py):
 *   motor_type_id → `motor_type`     MasterAHUMotorType.value__iexact       (:18)
 *   motor_efficiency_id → `efficiency` MasterAHUMotorEfficiency.value__iexact (:34)
 *   motor_enclosure_id → `enclosure`  MasterAHUMotorEnclosure.value__iexact  (:42)
 * The fans-CMS doc (`cms/1/…/fans/params/` — ONE tenant, AAF Flanders on dev3)
 * exposes only the SUBSET of enclosures/efficiencies/types that tenant's motors
 * use (e.g. enclosure holds just TEFC/TEAO — NOT ODP id 1, which 99 dev3 fans
 * carry), so a fan whose value is outside that subset is absent from
 * fanCmsOptionList. Such a row must resolve to null, NOT the raw numeric state
 * id: the backend would otherwise run `value__iexact=<int>` → Postgres
 * `upper(integer) does not exist` → HTTP 500 (blocked ALL rating for those
 * fans). Legacy's DOM fallback emitted a harmless string id (`upper('1')` — no
 * match, silently skipped, fan.selector.js collectData:4503-4515); mirror that
 * net effect with an explicit null so the backend's `if enclosure:` guard skips
 * it. Prefer `value` (what value__iexact queries), fall back to `name`. */
export const FAN_RATE_MOTOR_NAME_FIELDS = new Set([
  'motor_type_id',
  'motor_efficiency_id',
  'motor_enclosure_id',
]);

/** Resolve a motor NAME/value field's rate value from its CMS-doc row: the
 * master `value` (backend `value__iexact`), then `name`, else null. Never the
 * bare id — see FAN_RATE_MOTOR_NAME_FIELDS. */
export function fanMotorNameValue(row: Record<string, unknown> | undefined): string | null {
  if (!row) return null;
  const v = (row.value as unknown) ?? (row.name as unknown);
  return v == null || v === '' ? null : String(v);
}

// ---------------------------------------------------------------------------
// THE dependency graph (fan.selector.js:689-1212) — rooted at mfg_id, the
// LARGEST useDependencyGraph consumer. Compacted to the {dependencies,
// default_values} shape (firstLevelDependencies fold into dependencies — the
// engine reloads the whole descendant set on a parent change either way).
// ---------------------------------------------------------------------------

const MFG_DESCENDANTS = [
  'series_id', 'type_id', 'arr_id', 'rotation_id', 'amca_class_id',
  'speed_condition_id', 'redundancy_id', 'wheel_dia_percent_id',
  'wheel_width_percent_id', 'wheel_type_id', 'wheel_dia_nominal_id',
  'wheel_mtl_id', 'motor_type_id', 'motor_mfg_id', 'motor_series_id',
  'motor_power', 'cube_housing_id', 'style_id', 'mtr_supplied_by_id',
  'mtr_base_supplied_by_id', 'fan_isolators_supplied_by_id', 'isolator_type_id',
  'isolator_series_id', 'isolator_mfg_id', 'specified_deflection',
  'mounting_method_id', 'inlet_guard', 'outlet_guard', 'protective_cage',
  'fan_afs_sensor', 'fan_afs_transmitter', 'finish_id',
  'motor_shaft_grounding_id',
  'housed_discharge_id', 'motor_position_id', 'mounting_id',
];

export const FAN_DEPENDENCY_GRAPH: DependencyGraphSpec = {
  mfg_id: {
    dependencies: MFG_DESCENDANTS,
    // Dev-sync H: 237 slots in 2nd (fan.selector.js:820-829 on merged dev).
    default_values: [209, 237, 64, 23, 37, 47, 221, 164, 67],
  },
  type_id: {
    dependencies: [
      'series_id', 'arr_id', 'rotation_id', 'amca_class_id', 'speed_condition_id',
      'redundancy_id', 'wheel_dia_percent_id', 'wheel_width_percent_id',
      'wheel_type_id', 'wheel_dia_nominal_id', 'wheel_mtl_id', 'motor_type_id',
      'motor_mfg_id', 'motor_series_id', 'motor_power', 'cube_housing_id',
      'style_id', 'isolator_type_id', 'isolator_mfg_id', 'isolator_series_id',
      'specified_deflection', 'mtr_base_mfg_id', 'mtr_base_series_id',
      'fan_isolators_supplied_by_id', 'motor_shaft_grounding_id',
      'housed_discharge_id', 'motor_position_id', 'orientation_id', 'mounting_id',
    ],
    default_values: [1, 2, 4],
  },
  series_id: {
    dependencies: [
      'arr_id', 'speed_condition_id', 'redundancy_id', 'rotation_id',
      'amca_class_id', 'wheel_dia_percent_id', 'wheel_width_percent_id',
      'wheel_type_id', 'wheel_dia_nominal_id', 'wheel_mtl_id', 'motor_type_id',
      'motor_mfg_id', 'motor_series_id', 'motor_power', 'cube_housing_id',
      'style_id', 'mtr_base_mfg_id', 'mtr_base_series_id', 'mtr_supplied_by_id',
      'mtr_base_supplied_by_id', 'fan_isolators_supplied_by_id',
      'isolator_series_id', 'mounting_method_id', 'specified_deflection',
      'isolator_type_id', 'isolator_mfg_id', 'inlet_guard', 'outlet_guard',
      'protective_cage', 'fan_afs_sensor', 'fan_afs_transmitter', 'finish_id',
      'motor_shaft_grounding_id', 'housed_discharge_id', 'motor_position_id',
      'mounting_id',
    ],
    default_values: [
      // Tried in order against the loaded options (fan.selector.js:891-896):
      // 200 EPFN (TCF default) → 202 EPQN (TCF fallback if EPFN not available).
      200, 202, 844, 491, 492, 708, 709, 205, 173, 1391, 1389, 1390, 172, 175,
      174, 979, 980, 1112, 1113,
    ],
  },
  arr_id: {
    dependencies: [
      'mtr_base_mfg_id', 'mtr_base_series_id', 'mtr_supplied_by_id',
      'mounting_method_id', 'motor_position_id', 'isolator_type_id',
      'orientation_id', 'mtr_controller_id', 'mtr_base_supplied_by_id',
    ],
    default_values: [1, 4],
  },
  wheel_dia_percent_id: { dependencies: [], default_values: [100] },
  // PR #773 fan-series-fix: NO default — Wheel Width must never auto-fill
  // (was [100]; fan.selector.js:947-950).
  wheel_width_percent_id: { dependencies: [], default_values: [] },
  wheel_type_id: { dependencies: [], default_values: [] },
  wheel_dia_nominal_id: { dependencies: [], default_values: [] },
  wheel_mtl_id: { dependencies: [], default_values: [] },
  cube_housing_id: { dependencies: [], default_values: [2, 1] },
  redundancy_id: { dependencies: [], default_values: [1] },
  style_id: { dependencies: [], default_values: [1] },
  rotation_id: { dependencies: ['motor_position_id'], default_values: [1] },
  motor_type_id: {
    dependencies: [
      'motor_mfg_id', 'motor_series_id', 'motor_enclosure_id',
      'motor_efficiency_id', 'motor_housing_id', 'motor_power',
      'motor_shaft_grounding_id', 'mtr_controller_id', 'motor_speed_nominal_id',
    ],
    default_values: [1, 2],
  },
  mtr_supplied_by_id: {
    dependencies: [
      'fan_isolators_supplied_by_id', 'isolator_type_id', 'specified_deflection',
      'isolator_mfg_id', 'mtr_base_supplied_by_id',
    ],
    default_values: [],
  },
  mtr_base_supplied_by_id: { dependencies: [], default_values: [] },
  speed_condition_id: { dependencies: ['mtr_controller_id'], default_values: [2] },
  motor_voltage_id: { dependencies: ['motor_power'], default_values: [] },
  motor_mfg_id: {
    dependencies: [
      'motor_series_id', 'motor_enclosure_id', 'motor_efficiency_id',
      'motor_housing_id', 'motor_shaft_grounding_id', 'motor_power',
      'motor_speed_nominal_id',
    ],
    default_values: [79],
  },
  motor_series_id: {
    dependencies: [
      'motor_enclosure_id', 'motor_efficiency_id', 'motor_housing_id',
      'motor_shaft_grounding_id', 'motor_speed_nominal_id', 'motor_power',
    ],
    default_values: [116],
  },
  motor_enclosure_id: { dependencies: [], default_values: [1] },
  motor_efficiency_id: { dependencies: [], default_values: [1] },
  motor_housing_id: { dependencies: [], default_values: [1] },
  motor_shaft_grounding_id: { dependencies: [], default_values: [1] },
  motor_speed_nominal_id: { dependencies: [], default_values: [] },
  motor_position_id: { dependencies: [], default_values: [3, 4, 5, 2] },
  isolator_type_id: {
    dependencies: [
      'isolator_mfg_id', 'isolator_series_id', 'specified_deflection',
      'fan_isolators_supplied_by_id', 'mounting_method_id',
    ],
    default_values: [1],
  },
  specified_deflection: { dependencies: [], default_values: [] },
  mounting_method_id: { dependencies: [], default_values: [] },
  isolator_mfg_id: {
    dependencies: ['specified_deflection', 'isolator_series_id'],
    default_values: [99, 203, 1],
  },
  isolator_series_id: {
    dependencies: ['specified_deflection'],
    default_values: [178, 1154, 1],
  },
  fan_isolators_supplied_by_id: {
    dependencies: [
      'isolator_series_id', 'isolator_mfg_id', 'mounting_method_id',
      'specified_deflection',
    ],
    default_values: [1],
  },
  mtr_base_mfg_id: {
    dependencies: ['mtr_base_series_id', 'mtr_base_mtl_id'],
    default_values: [1],
  },
  mtr_base_series_id: { dependencies: ['mtr_base_mtl_id'], default_values: [1] },
  mtr_base_mtl_id: { dependencies: [], default_values: [1] },
  housed_discharge_id: { dependencies: ['motor_position_id'], default_values: [1] },
  orientation_id: { dependencies: [], default_values: [1] },
  mounting_id: { dependencies: [], default_values: [1] },
  amca_class_id: { dependencies: [], default_values: [] },
  motor_controller: { dependencies: [], default_values: [] },
  mtr_controller_id: { dependencies: [], default_values: [] },
  inlet_guard: { dependencies: [], default_values: [] },
  outlet_guard: { dependencies: [], default_values: [] },
  protective_cage: { dependencies: [], default_values: [] },
  fan_afs_sensor: { dependencies: [], default_values: [] },
  fan_afs_transmitter: { dependencies: [], default_values: [] },
  // Options-tab Finish. A leaf of mfg_id + series_id: the available finishes
  // are the MasterAHUFanMfgSeriesFinish rows for that pair, so the select
  // reloads with Mfg/Series like the guards and AFS lists do. `default_values`
  // is empty on purpose — the Mill preference is applied in FanSelector by
  // NAME (the Mill row's id is master data, not a constant), and the
  // sole-option rule already fills a mfg/series with exactly one finish.
  finish_id: { dependencies: [], default_values: [] },
};

/**
 * fan.selector.js:1524-1538 — `parametersForDifferentApi`: the params that hit
 * the master/fan_mfg_series branch (NOT the relative ahu/fans/params/ branch).
 */
const PARAMS_FOR_DIFFERENT_API = new Set<string>([
  'mtr_supplied_by_id', 'mtr_base_supplied_by_id', 'fan_isolators_supplied_by_id',
  'isolator_type_id', 'inlet_guard', 'outlet_guard', 'protective_cage',
  'fan_afs_sensor', 'fan_afs_transmitter', 'mounting_method_id', 'motor_power',
  'motor_shaft_grounding_id',
  // Finish joined this family when it became Mfg+Series dependent:
  // `master/.../fan_mfg_series/finish_id/?mfg_id=…&series_id=…`.
  'finish_id',
]);

/**
 * fan.selector.js loadParams (1522-1614) — the EXACT url construction per
 * parameter family. Returns null when a required parent is missing (legacy
 * returns []). `parentValues` maps each parent FIELD to its current value.
 *
 * The 4 url families (1565-1603):
 *   mtr_controller_id            → master/.../mtr_type/speed_condition/<p>/<filters>
 *   isolator_type_id             → cms/1/.../fans/<p>/<filters>
 *   other parametersForDifferentApi → master/.../fan_mfg_series/<p>/<filters>
 *   everything else (default)    → cms/1/.../fans/params/<p>/<filters>
 * Filters join non-null parents `name=value` with `&`; redundancy/speed_condition/
 * motor_position rebuild joining with `&fan_` and prefix `?fan_`.
 */
export function fanParamUrl(
  field: string,
  parents: string[],
  parentValues: Record<string, unknown>
): string | null {
  // Build the filter string from non-null parents (legacy 1544-1547).
  const pairs: Array<[string, unknown]> = [];
  for (const p of parents) {
    const v = parentValues[p];
    if (v != null && v !== '' && v !== 'None') pairs.push([p, v]);
  }
  // mtr_base_mtl_id with empty filters → [] (legacy 1548).
  if (field === 'mtr_base_mtl_id' && pairs.length === 0) return null;

  const joinAmp = (sep = '=') =>
    pairs.map(([k, v]) => `${k}${sep}${v}`).join('&');

  let filters: string;
  // prefix rules (1551-1564).
  if (field === 'mtr_base_series_id' || field === 'mtr_base_mfg_id') {
    // dev prefixes `fan_` to the FIRST key only — `filters = "?fan_" + filters`
    // (fan.selector.js:1943-1946), a plain string concat, NOT a per-key map.
    // That is load-bearing: MasterAHUFanMfgSeriesMtrBaseMfgSeriesFilter declares
    // `fan_mfg_id` but plain `series_id` / `arr_id` / `mtr_base_mfg_id`
    // (master/filter/motor.py:241-258), so dev's single-prefix string is exactly
    // what the FilterSet accepts. Prefixing every key made `fan_series_id` /
    // `fan_arr_id` unrecognised filter fields, which django-filter drops — the
    // query then narrowed on fan_mfg_id alone and returned motor-base rows for
    // any series/arrangement of that mfg. Also match dev's substring test on the
    // joined string (`mtr_base_mfg_id` contains "mfg_id"), not an exact key test.
    filters = joinAmp();
    const hasFanRoot = ['type_id', 'mfg_id', 'series_id', 'arr_id'].some((k) =>
      filters.includes(k)
    );
    filters = hasFanRoot ? `?fan_${filters}` : `?${filters}`;
  } else if (
    field === 'redundancy_id' ||
    field === 'speed_condition_id' ||
    field === 'motor_position_id'
  ) {
    filters = `?fan_${pairs.map(([k, v]) => `${k}=${v}`).join('&fan_')}`;
  } else {
    filters = `?${joinAmp()}`;
  }
  if (pairs.length === 0) filters = '';

  // family selection (1565-1603).
  if (field === 'mtr_controller_id') {
    // arr_id → fan_arr_id in the filters first (1568).
    const f = filters.replace(/\barr_id=/g, 'fan_arr_id=');
    return `${MASTER}/mtr_type/speed_condition/${field}/${f}`;
  }
  if (field === 'isolator_type_id') {
    return `${CMS}/fans/${field}/${filters}`;
  }
  if (PARAMS_FOR_DIFFERENT_API.has(field)) {
    return `${MASTER}/fan_mfg_series/${field}/${filters}`;
  }
  // default — the relative ahu/fans/params/ branch.
  return `${CMS}/fans/params/${field}/${filters}`;
}

/** map a state `_id`/short field to its envelope `name` (collectData). */
export function shortName(field: string): string {
  return FAN_FIELD_SHORT_NAMES[field] ?? field;
}

// ---------------------------------------------------------------------------
// PURE vendor builders (the verifier reads these for the §32 byte diff).
// ---------------------------------------------------------------------------

/**
 * fan.selector.js rate() (3154-3345) — the per-mfg input stripping. Given the
 * collected `data` (collectData output) + mfg + the fan type value, returns the
 * envelope `input` (the stripping is the per-mfg quirk; buildRatingEnvelope adds
 * manufacturer/function_name/type/construction_class_id).
 *
 *   rosenberg(164): strip ALL isolator/mtr keys.
 *   TCF-family (64/152/221/209): strip isolator/mtr EXCEPT mtr_controller_id.
 *   greenheck/ebm/ZA/comefri: no key stripping.
 * construction_class_id appended from the model when present.
 */
/** Numeric search/input fields the legacy collectData coerces via
 * `Number(value.replace(/,/g,''))` (fan.selector.js:3438). NY Blower's backend
 * does str/int math on these and crashes on a string "20000" (SECONDARY bug).
 * Send NUMBERS, comma-stripped. Non-numeric leftovers are left untouched. */
const FAN_NUMERIC_RATE_FIELDS = [
  'airflow_actual', 'airflow_per_fan', 'tsp', 'fan_inlet_pressure',
  'fan_inlet_temp', 'max_peak_pressure_percent', 'qty_wide', 'qty_high', 'qty',
  'elevation', 'aw_width', 'aw_height',
];

export function buildFanRateInput(
  data: Record<string, unknown>,
  mfgId: number,
  constructionClassId: number | null | undefined,
  // KA-5254 — the current fan slot's identity for the backend AW computation
  // (fan.selector.js rate() :4079-4092 on merged dev). ahuFanId = the AHUFan
  // component id (legacy current_model.id); masterMotorId = the chosen/saved
  // motor ((motor_choosen || selectedMotorData)?.id || model.master_motor_id).
  awCtx?: { ahuFanId: number | null; masterMotorId: number | string | null }
): Record<string, unknown> {
  const input: Record<string, unknown> = { ...data };
  const minAw = FAN_MIN_AW_MFG_IDS.has(mfgId);
  // SECONDARY: coerce the numeric rate inputs to Number (comma-stripped). Skip
  // null/blank so we never inject 0 where the field was absent.
  for (const k of FAN_NUMERIC_RATE_FIELDS) {
    if (k in input) {
      const n = toNum(input[k]);
      if (n != null) input[k] = n;
    }
  }
  if (mfgId == 164) {
    // rosenberg: strip ALL isolator/mtr keys — EXCEPT isolator keys for the
    // MIN_AW mfgs (KA-5254 — the backend resolves them for calc_required_aw;
    // fan.selector.js :4011-4021 on merged dev; 164 IS in the set).
    for (const k of Object.keys(input)) {
      if ((k.includes('isolator') || k.includes('mtr')) && !(minAw && k.includes('isolator'))) {
        delete input[k];
      }
    }
  } else if (mfgId == 64 || mfgId == 152 || mfgId == 221 || mfgId == 209 || mfgId == 237) {
    // TCF-family (+ Annexair 237, dev-sync H): strip isolator/mtr EXCEPT
    // mtr_controller_id AND mtr_safety (fan.selector.js:4139-4148 on merged
    // dev — `!["mtr_controller_id", "mtr_safety"].includes(i)`) — and EXCEPT
    // isolator keys for the MIN_AW mfgs (KA-5254, js:4059-4072; 64/221/209/237
    // in the set, basx 152 NOT — its isolator keys still strip).
    for (const k of Object.keys(input)) {
      if (
        (k.includes('isolator') || k.includes('mtr')) &&
        !['mtr_controller_id', 'mtr_safety'].includes(k) &&
        !(minAw && k.includes('isolator'))
      ) {
        delete input[k];
      }
    }
  }
  // construction_class_id appended (3308-3311).
  if (constructionClassId != null) input.construction_class_id = constructionClassId;
  // KA-5254 — ahu_fan_id + master_motor_id for the MIN_AW mfgs (js:4081-4092).
  if (minAw && awCtx?.ahuFanId) {
    input.ahu_fan_id = awCtx.ahuFanId;
    const existingMotorId = awCtx.masterMotorId;
    if (existingMotorId) {
      const n = toNum(existingMotorId);
      if (n != null) input.master_motor_id = n;
    }
  }
  return input;
}

/**
 * fan.selector.js getFanData() (2967-3141) — the SECOND /rating/ POST per-fan
 * curve fetch. Builds the {manufacturer, part, type, input, function_name}
 * envelope per the curve-vendor quirk:
 *   comefri(23):   function_name 'get_curve_data', input = {...data, ...choosen}
 *   ZA(67):        function_name 'GET_CURVE', input = the explicit mpn block
 *   ebm(37):       function_name 'GET_FAN_CURVE', input = choosen
 *   rosenberg(164):function_name 'get_fan_data', input = choosen
 * Returns null when type is unresolved (legacy `if (!data.type) return`).
 */
export function buildFanCurveEnvelope(
  mfgId: number,
  fanType: string | undefined,
  data: Record<string, unknown>,
  choosen: Record<string, any>
): Record<string, unknown> | null {
  if (!fanType) return null;
  if (mfgId == 23) {
    return {
      manufacturer: 'comefri', part: 'fan', type: fanType,
      input: { ...data, ...choosen }, function_name: 'get_curve_data',
    };
  }
  if (mfgId == 67) {
    return {
      manufacturer: 'ziehl_abegg', part: 'fan', type: fanType,
      input: {
        mpn: choosen.mpn,
        airflow_per_fan: +choosen.airflow_per_fan,
        airflow_actual: +choosen.airflow_actual,
        tsp: +choosen.tsp,
        elevation: data.elevation || 0,
        qty: choosen.qty || 1,
        qty_wide: choosen.qty_wide || 1,
        qty_high: choosen.qty_high || 1,
      },
      function_name: 'GET_CURVE',
    };
  }
  // ebm(37) / rosenberg(164).
  return {
    manufacturer: mfgId == 37 ? 'ebm_papst' : 'rosenberg', part: 'fan',
    type: fanType, input: choosen,
    function_name: mfgId == 37 ? 'GET_FAN_CURVE' : 'get_fan_data',
  };
}

const toNum = (v: unknown): number | null => {
  if (v == null || v === '' || v === 'None' || v === '--') return null;
  const n = Number(String(v).replace(/,/g, ''));
  return Number.isNaN(n) ? null : n;
};

/** legacy generetionTimeStamp (fan.selector.js) — "YYYY.MM.DD_HH:MM:SS".
 * rating_datetime is a PERSISTED varchar; the port stored
 * `new Date().toLocaleString()` ("7/9/2026, 2:51:04 AM") which breaks the
 * stored-format parity — P1-verify fix, 2026-07-09. */
export function fanRatingTimestamp(d: Date = new Date()): string {
  const p = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())}_${p(
    d.getHours()
  )}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/**
 * fan.selector.js getModelFanParams() (5027-5149) — the chosen-row → model /
 * params_fan OVERLAY that runs BEFORE the selectFan write (called from
 * chooseFan js:5293 and again from selectFan js:5922). P1b port, 2026-07-09:
 * without it, Select persisted the fan with its STALE pre-rate performance
 * outputs (speed/bhp/sound powers kept their old values).
 *
 * Legacy semantics, key by key over the CHOSEN rated row (`self.choosen`):
 *  - parameters_collections keys (js:5030-5089) are VALUE→CMS-id remapped into
 *    params_fan (`key + "_id"`; arr_id keeps its name, js:5114-5120; no CMS
 *    match writes an explicit null). They do NOT land on the model.
 *  - `style` is consumed but assigned NOWHERE (the js:5103-5107 branch looks
 *    up `parameters_collections[key].id` — undefined — and never assigns).
 *  - `speed_max` → model.speed_max AND model.rated_fan_speed_max (js:5122-5124)
 *  - `density`   → model.air_density (js:5125-5126)
 *  - EVERY other key overlays the model verbatim (js:5127-5129) — this is what
 *    carries speed, bhp, dbi_63..8000, dbo_63..8000, efficiencies, outlet_vel
 *    etc. into the PATCH.
 *  - post-loop stamps (js:5131-5137): rotation_id / housed_discharge_id /
 *    speed_condition_id / redundancy_id from the LIVE selects. (Legacy caches
 *    these on change events — untouched selects stamp `undefined`, which
 *    JSON.stringify drops; the port reads the state model, which IS the select
 *    value store, so the stamped value always matches what the select shows.)
 *  - mfg 164/37/67 → params_fan.fan_type / style_id (js:5138-5145);
 *    mfg 23 → params_fan.id = choosen.fan_id (js:5146-5148).
 *
 * NOTE params_fan is REBUILT from scratch (js:5090 `params_fan = {}`) — the
 * stored fan_params blob does NOT bleed into it.
 *
 * Port-only exclusions from the model overlay (documented deviations):
 *  - `__curve` — React-only per-row curve cache, not row data.
 *  - bhp_curve/rpm_curve/system_curve/CFMPoints — legacy deletes them off
 *    choosen before the selectFan-time overlay (js:5909-5912); they persist
 *    through PUT ahu/fans/curves/<id> instead (and `delete m.curve` keeps the
 *    PATCH curve-free — T1).
 *  - id/section_id — guard the shim model's identity; rows for the mfgs on
 *    this path don't carry them, and overwriting either would corrupt the
 *    PATCH target.
 */
const FAN_CHOSEN_PARAM_COLLECTIONS: Record<string, { path: string; key: string }> = {
  amca_class: { path: 'fan.amca_classes', key: 'name' },
  speed_nominal: { path: 'fan.motor.speed_nominal', key: 'name' },
  wheel_size: { path: 'fan.wheel.size', key: 'value' },
  wheel_width_percent: { path: 'fan.wheel.width_percent', key: 'value' },
  wheel_dia_percent: { path: 'fan.wheel.dia_percent', key: 'value' },
  wheel_mtl: { path: 'fan.wheel.material', key: 'value' },
  series: { path: 'fan.series', key: 'series' },
  style: { path: 'fan.style', key: 'id' },
  rotation: { path: 'fan.rotation', key: 'value' },
  rotation_id: { path: 'fan.rotation', key: 'value' },
  wheel_dia_nominal: { path: 'fan.wheel.nominal_diameter', key: 'value' },
  cube_housing: { path: 'fan.cube_housing', key: 'value' },
  housed_discharge_id: { path: 'fan.housed_discharge', key: 'value' },
  // legacy hardcodes the dwdi_arr3 collection here (js:5081-5084).
  motor_position_id: { path: 'fan.motor_position.dwdi_arr3', key: 'value' },
  arr_id: { path: 'fan.arrangement', key: 'id' },
};

const FAN_CHOSEN_MODEL_EXCLUDED = new Set<string>([
  '__curve', 'bhp_curve', 'rpm_curve', 'system_curve', 'CFMPoints',
  'id', 'section_id',
]);

export function buildModelFanParams(
  s: Record<string, any>,
  choosen: Record<string, any>,
  doc: FanCmsDoc | undefined
): { model: Record<string, any>; paramsFan: Record<string, any> } {
  const model: Record<string, any> = {};
  const paramsFan: Record<string, any> = {};

  for (const key of Object.keys(choosen)) {
    if (FAN_CHOSEN_MODEL_EXCLUDED.has(key)) continue;
    const spec = FAN_CHOSEN_PARAM_COLLECTIONS[key];
    if (spec) {
      if (key === 'style') continue; // js:5103-5107 — a no-op branch (assigns nothing)
      const option = fanCmsOptionListByPath(doc, spec.path).find(
        (r: any) => r[spec.key] == choosen[key]
      );
      if (key === 'arr_id') paramsFan.arr_id = option ? option.id : null;
      else paramsFan[`${key}_id`] = option ? option.id : null;
    } else if (key === 'speed_max') {
      model.speed_max = choosen[key];
      model.rated_fan_speed_max = choosen[key];
    } else if (key === 'density') {
      model.air_density = choosen[key];
    } else {
      model[key] = choosen[key];
    }
  }

  // js:5131-5137 — live-select stamps. Empty stamps drop the key, matching
  // legacy's `undefined` assignment being omitted by JSON.stringify.
  const stamp = (k: string, v: unknown) => {
    if (v == null || v === '' || v === '--') delete paramsFan[k];
    else paramsFan[k] = v;
  };
  stamp('rotation_id', s.rotation_id);
  stamp('housed_discharge_id', s.housed_discharge_id ?? s.housed_discharge);
  stamp('speed_condition_id', s.speed_condition_id ?? s.speed_condition);
  stamp('redundancy_id', s.redundancy_id ?? s.redundancy);

  const mfgId = Number(s.mfg_id);
  if (mfgId == 164 || mfgId == 37 || mfgId == 67) {
    paramsFan.fan_type = choosen?.fan_type;
    paramsFan.style_id = choosen?.style;
  }
  if (mfgId == 23) paramsFan.id = choosen?.fan_id;
  // Dev-sync H — annexair (fan.selector.js getModelFanParams :5366-5391 on
  // merged dev):
  //  - fan_inlet_temp_wb is a required Annexair input but never part of the
  //    vendor result, so the loop above never copies it — carry the live
  //    user-entered value forward or it's dropped from the saved fan_params
  //    and the required-fields check fails forever on reopen.
  //  - Annexair's vendor "wheel_dia_nominal" is the physical wheel diameter in
  //    INCHES, not a KSDM id — the generic value→id remap above lands on an
  //    unrelated row (or null). The rating API returns the KSDM-resolved
  //    wheel_dia_nominal_id directly — use that instead of the guessed value.
  if (mfgId == 237) {
    paramsFan.fan_inlet_temp_wb = s.fan_inlet_temp_wb;
    paramsFan.wheel_dia_nominal_id = choosen?.wheel_dia_nominal_id ?? null;
  }

  return { model, paramsFan };
}

/**
 * The chosen fan's identity + rated results, sent in the /select POST body
 * itself. They used to reach the server only through the auto-save PATCH
 * fired from currentModelSet after the POST commits — a PATCH that silently
 * no-ops when the fan form's shim is missing/unarmed. When it was skipped the
 * POST still saved the NEW fan_params/motor, so the fan kept its OLD
 * master_fan (staging AHU 3914: a Ziehl-Abegg plug selection left a Twin City
 * EPFN plenum master_fan under a ZA EC motor; nothing server-side repairs it —
 * AHUFan.save only resolves master_fan from fan_params.id, which only mfg 23
 * stamps). All are writable AHUFanSerializer columns.
 */
export const SELECT_RESULT_KEYS = [
  'master_fan_id', 'master_motor_id',
  'speed', 'rated_fan_speed_max', 'rating_datetime', 'rating_id',
  'bhp', 'motor_power', 'bp_kw', 'rating_motor_fla', 'rating_motor_fla_total',
  'outlet_vel', 'static_efficiency', 'total_efficiency', 'mech_efficiency',
  'air_density', 'fei', 'rpi', 'qty_wide', 'qty_high', 'fan_qty',
  'dbi_63', 'dbi_125', 'dbi_250', 'dbi_500', 'dbi_1000', 'dbi_2000', 'dbi_4000', 'dbi_8000',
  'dbo_63', 'dbo_125', 'dbo_250', 'dbo_500', 'dbo_1000', 'dbo_2000', 'dbo_4000', 'dbo_8000',
] as const;

/** Sent only when the chosen row carries one: partial_update 400s the whole
 * request on an empty or unknown key (fan_viewset.py "wrong key"), and the
 * stored value spread in from the detail can be null. */
const SELECT_REGISTRY_KEYS = [
  'fan_cost_registry_key', 'motor_cost_registry_key', 'motor_base_cost_registry_key',
  'isolator_cost_registry_key',
] as const;

/**
 * fan.selector.js selectFan() (4705-4945) — the Apply WRITE shape (board-mode,
 * select=true). PURE so the verifier reads the exact ~60-field model_data.model
 * + the dataForSet {air_stream_id, airflow_actual, motor_params, fan_params,
 * ...SELECT_RESULT_KEYS} (the /select POST body).
 *
 * `s` = the live state model toJSON; `choosen` = the chosen rated fan row;
 * `modelData` = {model, params_fan, project}; `motorChoosen` = the chosen motor
 * (or motorModels[0]); `genericMfg` from the mfg <option> data-generic; ctx
 * carries the resolved style/speed/redundancy/rateing_time + isolatorSaveData.
 *
 * Rosenberg deviation (4856-4865): si_params/mounting_method stripped, style_id
 * saved instead.
 */
export function buildFanSelectPayload(
  s: Record<string, any>,
  choosen: Record<string, any>,
  modelData: { model: Record<string, any>; params_fan: Record<string, any> },
  motorChoosen: Record<string, any> | null,
  ctx: {
    genericMfg: boolean;
    ratingDatetime: string | null;
    fanDataId: number | null;
    /** The Finish the dialog was SHOWING (`FanSelector.shownFinishId`) — the
     * same value the rating envelope and the catalog re-match used, and the one
     * stored. null when the Options tab draws no Finish select at all. */
    finishId: number | null;
    motorBaseVisible: boolean;
    mtrSuppliedById: number | null;
    isolatorSaveData: Record<string, any> | null;
    ratedNotes?: unknown;
  }
): {
  currentModelSet: Record<string, unknown>;
  isolatorSet: Record<string, unknown> | null;
  dataForSet: Record<string, unknown>;
} {
  const mfgId = Number(s.mfg_id);
  const m: Record<string, any> = { ...modelData.model };
  const pf: Record<string, any> = { ...modelData.params_fan };

  // si_params is a nested object until JSON.stringify'd at the end.
  const siParams: Record<string, any> = m.si_params && typeof m.si_params === 'object' ? { ...m.si_params } : {};

  m.fei = choosen.fei || null;
  // RPI — persist the chosen candidate's rpi (legacy fan.selector.js 4759:
  // self.model_data.model['rpi'] = self.converToNumber(self.choosen.rpi)).
  m.rpi = toNum(choosen.rpi);
  m.speed_condition_id = toNum(s.speed_condition_id ?? s.speed_condition);
  m.redundancy_id = toNum(s.redundancy_id ?? s.redundancy);
  m.pricing_method_id = toNum(m.pricing_method_id);
  m.cost_product_user = toNum(m.cost_product_user);
  m.cost_freight_user = toNum(m.cost_freight_user);
  m.net_price_product_user = toNum(m.net_price_product_user);
  m.net_price_freight_user = toNum(m.net_price_freight_user);
  m.motor_pricing_method_id = toNum(m.motor_pricing_method_id);
  m.motor_cost_product_user = toNum(m.motor_cost_product_user);
  m.motor_cost_freight_user = toNum(m.motor_cost_freight_user);
  m.motor_net_price_product_user = toNum(m.motor_net_price_product_user);
  m.motor_net_price_freight_user = toNum(m.motor_net_price_freight_user);
  m.master_fan_id = toNum(ctx.fanDataId);
  m.airflow_per_fan = toNum(s.airflow_per_fan);
  m.motor_position_id = toNum(s.motor_position_id);
  m.mtr_controller_id = toNum(s.mtr_controller_id);
  m.fan_isolators_supplied_by_id = toNum(s.fan_isolators_supplied_by_id);
  siParams.isolator_type_id = toNum(s.isolator_type_id);
  siParams.series_id = toNum(s.isolator_series_id);
  m.air_stream_id = toNum(s.air_stream_id);
  m.mtr_supplied_by_id = toNum(ctx.mtrSuppliedById);
  // Selector-parity FAN fix — mtr_base_supplied_by_id IS a real serializer
  // column (fan_serializers.py:189/:290) and legacy carries it alongside
  // mtr_supplied_by_id everywhere (fan.selector.js:544/:551, :858-859), but the
  // React select path never stamped it — a live "Supplied by" edit on the
  // motor-base sub-tab was dropped. Stamp it from state like its sibling.
  m.mtr_base_supplied_by_id = toNum(s.mtr_base_supplied_by_id);
  m.inlet_guard_id = toNum(s.inlet_guard);
  m.outlet_guard_id = toNum(s.outlet_guard);
  m.protective_cage_id = toNum(s.protective_cage);
  m.fan_afs_sensor_id = toNum(s.fan_afs_sensor);
  m.fan_afs_transmitter_id = toNum(s.fan_afs_transmitter);
  // Finish: the finish the DIALOG WAS SHOWING (`shownFinishId`, resolved by the
  // caller) — the very value the rating envelope filtered on and the catalog
  // re-match narrowed with, so a fan is stored with the finish it was rated and
  // matched on. Three things that must agree, read once.
  //
  // Not the state value raw, and not the resolved catalog row's own
  // MasterAHUFan.finish:
  //  - the catalog row's finish stored the wrong one whenever the two disagreed
  //    — a Mill rating whose re-match landed on the Rust Anode row came back as
  //    `finish_id: 2` (box-anna, 2026-10-03);
  //  - the raw state value can hold a finish with no select to show it (a
  //    Mfg/Series with no MasterAHUFanMfgSeriesFinish row), which the rating
  //    rightly ignores — storing it anyway would leave the saved finish
  //    disagreeing with the fan that was actually rated.
  // Nothing is lost by storing null there: both display paths already fall back
  // to the catalog row themselves (`obj.finish or obj.master_fan.finish`,
  // cad_serializers.py:576 for Model View, serializer_technical_data.py:440 for
  // the Technical Data report).
  m.finish_id = toNum(ctx.finishId);
  pf.mfg_id = toNum(s.mfg_id);
  pf.series_id = toNum(s.series_id);
  pf.type_id = toNum(s.type_id);
  pf.wheel_mtl_id = toNum(s.wheel_mtl) || m.wheel_mtl_id || null;
  m.tsp = toNum(s.tsp);
  m.esp_desired = toNum(s.esp_desired);
  // Dev-sync H: fan_inlet_temp + mtr_safety are model columns whose inputs are
  // legacy `.fan_field`s — every edit lands on model_data.model via
  // changeFanField (fan.selector.js:4697 `self.model_data.model[name]=value`)
  // and rides the select PATCH (mtr_safety is a serializer field since
  // ahu/0133). Stamp from state only when the input holds a value — an
  // untouched blank never wrote to the legacy model either.
  if (s.fan_inlet_temp != null && s.fan_inlet_temp !== '') {
    m.fan_inlet_temp = toNum(s.fan_inlet_temp);
  }
  // Selector-parity FAN restore — "Design" temp, the non-Annexair partner of
  // fan_inlet_temp (fan.selector.tmpl:527). A `.fan_field` model column; stamp
  // only when the input holds a value (matches the fan_inlet_temp guard above).
  if (s.fan_inlet_design != null && s.fan_inlet_design !== '') {
    m.fan_inlet_design = toNum(s.fan_inlet_design);
  }
  if (s.mtr_safety != null && s.mtr_safety !== '') {
    m.mtr_safety = toNum(s.mtr_safety);
  }
  // Performance-tab "Max % of Peak" and "Inlet Pressure" (fan.selector.tmpl:
  // 473-475 / :491). Same shape as the qty selects below and the temps above:
  // `search_field fan_field` inputs rendering `model.<name>` off real AHUFan
  // columns (models_component_fan.py:248/:257, serializer fields :139/:142),
  // seeded back from the model on reopen (SEED_MODEL_FIELDS) — but the port
  // routed both through onSearchField only, so an edit reached `state` and the
  // rate body while the Select PATCH kept re-sending the STORED value. Stamp
  // from live state, guarded like fan_inlet_temp/mtr_safety above so a blank
  // input cannot null a stored value.
  if (s.max_peak_pressure_percent != null && s.max_peak_pressure_percent !== '') {
    m.max_peak_pressure_percent = toNum(s.max_peak_pressure_percent);
  }
  if (s.fan_inlet_pressure != null && s.fan_inlet_pressure !== '') {
    m.fan_inlet_pressure = toNum(s.fan_inlet_pressure);
  }
  m.rating_datetime = ctx.ratingDatetime;
  m.wheel_dia_nominal = toNum(choosen.wheel_dia_nominal);
  // No housed_discharge_id stamp: a DWDI fan's housing discharge belongs to the
  // catalogue row, the AHUFan has no column for it, and the serializer derives
  // it from the row (fan_serializers.py `housed_discharge_id`, read-only). The
  // legacy line here read `s.housed_discharge` — a key nothing ever sets, so it
  // only ever PATCHed null. The user-facing select still rides fan_params
  // (buildModelFanParams's `housed_discharge_id` stamp) as it always did.
  m.fan_spacing_sides_id = toNum(s.fan_spacing_sides_id);
  m.fan_spacing_top_btm_id = toNum(s.fan_spacing_top_btm_id);
  m.fan_spacing_ud_top_btm = toNum(s.fan_spacing_ud_top_btm) || null;
  m.fan_spacing_ud_sides = toNum(s.fan_spacing_ud_sides) || null;
  // Selector-parity FAN restore — the Mounting-tab assembly/bulkhead geometry
  // columns (fan.selector.tmpl:797-855). Legacy `.fan_field`s that ride
  // model_data.model on every changeFanField edit (js:4697) and PATCH on Select;
  // these already sit in `m` via `{...detail}`, so stamping the live state value
  // is what makes a user EDIT persist (parity with the fan_spacing_* stamps).
  m.assy_height_from_floor = toNum(s.assy_height_from_floor);
  m.assy_distance_from_ceiling = toNum(s.assy_distance_from_ceiling);
  m.assy_distance_from_section_ups = toNum(s.assy_distance_from_section_ups);
  m.bulkhead_wall_to_ahu_side_wall = toNum(s.bulkhead_wall_to_ahu_side_wall);
  // Performance-tab get_isp_esp columns (fan.selector.tmpl:541/555) — legacy
  // setDischargeAndEffencity writes both onto model_data.model (js:3658-3659)
  // and they feed the ISP/ESP → rating lookup. dwdi (type 4) carries a null
  // discharge_id + system_effect_id 20; every other type the reverse.
  m.discharge_id = toNum(s.discharge_id);
  m.system_effect_id = toNum(s.system_effect_id);
  // Motor-tab Voltage column (fan.selector.tmpl:1117) — a `.fan_field`; the
  // rate ⑤ block already resolves it, this makes the picked id survive Select.
  m.motor_voltage_id = toNum(s.motor_voltage_id);
  m.mounting_id = toNum(s.mounting_id);
  m.orientation_id = toNum(s.orientation_id);
  m.generic_mfg = ctx.genericMfg;
  m.outlet_vel = Math.round(toNum(m.outlet_vel) ?? 0);
  m.bp_kw = choosen.bp_kw;
  m.rating_motor_fla = choosen.fla || null;
  m.rating_motor_fla_total = choosen.fla_total || null;
  m.motor_power = +(mfgId == 67 || mfgId == 37 || mfgId == 164
    ? choosen.motor_power
    : choosen.power || choosen.motor_power);
  // Qty Wide / Qty High are `search_field fan_field` in legacy
  // (fan.selector.tmpl:281/291), so changeFanField writes each straight onto
  // model_data.model (js:4630) and the Select PATCH carries the live value.
  // The port routes both selects through onSearchField ONLY — they reached
  // `state` but never the model, so `m` kept the STORED qty spread in from
  // `detail` and Select persisted the pre-edit value. Rating 2x2 then
  // selecting saved 1x1, and the reopen seed (SEED_MODEL_FIELDS reads
  // model.qty_wide/qty_high) showed 1x1 back. Stamp from state like every
  // other fan_field below.
  // (guarded: an unset select must not null a stored qty — legacy only
  // overwrites model_data.model on an actual change event.)
  //
  // ★ Qty Wide x High `--` (Annexair, 2026-10-07): with the select left on `--`
  // there is no array in `state` to stamp — the array is the one the user chose
  // in the rated row's Wide x High select, which rides the row as
  // `qty_wide`/`qty_high` (AnnexairFanHandler._attach_wide_high). Read the row
  // in that case so Select persists the configuration the numbers on screen
  // belong to. (State used to win whenever it held a real value; that stopped
  // being right when a named array started coming back with its equivalents —
  // see below.)
  //
  // ★ …and since 2026-10-19 the row wins whenever that select offered a CHOICE,
  // `--` or not. A rating that names its array now comes back with the arrays
  // equivalent to it as well — rate 2x2 and the column offers 1x4 and 4x1, one
  // vendor call answering all three (`qty_expansion.equivalent_layouts`). The
  // user picking 1x4 there has to be what gets saved; reading `state` first
  // would save the 2x2 they searched with, silently, and the reopen would show
  // it back. `wide_high_options` is the precise condition — it is only present
  // when the column rendered a list to pick from.
  const rowArray = ratedRowArray(choosen);
  const qtyFromRatedRow =
    rowArray != null || fanQtyIsUnset(s.qty_wide) || fanQtyIsUnset(s.qty_high);
  const qtyWide = rowArray ? rowArray.wide
    : fanQtyIsUnset(s.qty_wide) ? toNum(choosen.qty_wide) : toNum(s.qty_wide);
  const qtyHigh = rowArray ? rowArray.high
    : fanQtyIsUnset(s.qty_high) ? toNum(choosen.qty_high) : toNum(s.qty_high);
  if (qtyWide != null) m.qty_wide = qtyWide;
  if (qtyHigh != null) m.qty_high = qtyHigh;
  // fan_qty off the SAME pair (it used to multiply the raw state values, which
  // is 0 for an unset select), minus the standby fan when Redundancy is on.
  //
  // ⚠ `s.redundancy_id ?? s.redundancy`, the LIVE value, as `calcAirflow`,
  // `collectData`, the rate envelope and `m.redundancy_id` all read it. Only
  // the mount seed ever writes `s.redundancy`; the Redundancy select writes
  // `redundancy_id` through `onSearchField`, so reading `s.redundancy` alone
  // is the value the dialog OPENED with. It was harmless while this ran only
  // for a `--` rating (where the user had just picked the array and the
  // redundancy with it), and stopped being harmless when the row started
  // winning for an explicit array too: switching N -> N+1, naming 2x2 at
  // 10 000 CFM and pressing Select saved 2 500 per fan where it should save
  // 3 333.
  const effQty = (toNum(m.qty_wide) ?? 1) * (toNum(m.qty_high) ?? 1);
  const redundancy = s.redundancy_id ?? s.redundancy;
  m.fan_qty = !redundancy || redundancy == 1 ? effQty : effQty - 1;
  // The array came from the rated row, so the per-fan figure has to be
  // RE-DERIVED from it — whatever is in the Airflow-per-fan box was typed (or
  // left over) while Qty Wide x High said `--`, i.e. before there was an array
  // to divide by, so it need not match the configuration being saved. It is not
  // cosmetic: `airflow_per_fan` arriving in a PATCH without `airflow_actual`
  // makes the fan viewset recompute the stored TOTAL as
  // `airflow_per_fan * qty_wide * qty_high` (api/rest_ahu/fan_viewset.py), so a
  // mismatched figure silently rewrites the unit's airflow.
  //
  // Off the LIVE total, which is what `calcAirflow` divides for an explicit
  // qty — so `--` plus a 4x2 pick stamps exactly what picking 4x2 in the Fan
  // tab would have. (It used to be skipped entirely for an explicit qty, and
  // since the row started winning there it is not — which is why `fan_qty`
  // above had to stop reading a stale redundancy.) With an explicit qty and an
  // unchanged redundancy nothing moves: state already holds
  // the computed value and the first condition is false.
  if ((qtyFromRatedRow || m.airflow_per_fan == null) && (m.fan_qty as number) > 0) {
    const total = toNum(s.airflow_actual ?? m.airflow_actual);
    if (total != null) m.airflow_per_fan = total / (m.fan_qty as number);
  }
  pf.notes = ctx.ratedNotes;
  // dev-sync F Stage 2 (fan.selector.js:6242-6246, commit ab531dff4d):
  // "Like Wheel Diameter and Wheel Width Percent, Wheel Type is a rating search
  // filter, not a property of the selected fan - leave it unset ('--') after
  // selection instead of carrying over whatever filter value was used to rate."
  // was: pf.wheel_type_id = s.wheel_type_id;
  pf.wheel_type_id = null;
  if (mfgId == 37) {
    m.static_efficiency = choosen.static_efficiency;
    m.total_efficiency = choosen.total_efficiency;
  }
  // motor base params (4815-4821).
  m.motor_base_params = ctx.motorBaseVisible
    ? JSON.stringify({
        mfg_id: toNum(s.mtr_base_mfg_id),
        series_id: toNum(s.mtr_base_series_id),
        mtl_id: toNum(s.mtr_base_mtl_id),
      })
    : null;
  if (motorChoosen) {
    m.master_motor_id = motorChoosen.id;
    if (motorChoosen.power && !m.motor_power) m.motor_power = motorChoosen.power;
  }
  if (!m.mounting_method_id && s.mounting_method_id) m.mounting_method_id = toNum(s.mounting_method_id);

  // cost registry keys (4837-4851).
  if (choosen.fan_cost_registry_key) m.fan_cost_registry_key = choosen.fan_cost_registry_key;
  if (choosen.motor_cost_registry_key) m.motor_cost_registry_key = choosen.motor_cost_registry_key;
  if (choosen.motor_base_cost_registry_key) m.motor_base_cost_registry_key = choosen.motor_base_cost_registry_key;
  if (choosen.isolator_cost_registry_key) m.isolator_cost_registry_key = choosen.isolator_cost_registry_key;
  if (choosen.rating_id) m.rating_id = choosen.rating_id;

  // Rosenberg deviation (4856-4865): strip si_params/mounting_method, save style_id.
  let isolatorSet: Record<string, unknown> | null = null;
  if (mfgId == 164) {
    delete m.si_params;
    delete m.mounting_method_id;
    delete m.si_specified_deflection_id;
    delete pf.si_specified_deflection_id;
    delete m.fan_isolators_supplied_by_id;
    m.style_id = toNum(s.style_id);
  } else {
    m.si_params = JSON.stringify(siParams);
    // non-rosenberg also writes isolatorSaveData onto the model (4874-4879).
    if (ctx.isolatorSaveData) {
      isolatorSet = { ...ctx.isolatorSaveData };
      delete (isolatorSet as Record<string, unknown>).fmbase;
    }
  }
  delete m.wheel_mtl_id;
  delete m.discharge_vel;
  // Legacy never PATCHes `curve` (changed-keys-only save + explicit curve strip,
  // fan.selector.js:6260); DRF rejects it as a writable nested field → 500.
  // Curve persistence goes through PUT /ahu/fans/curves/<id> instead.
  delete m.curve;

  // dataForSet (4889-4909).
  const dataForSet: Record<string, unknown> = {
    air_stream_id: toNum(m.air_stream_id),
    airflow_actual: toNum(m.airflow_actual == null ? s.airflow_actual : m.airflow_actual),
  };
  // motor_params rebuild (fan.selector.js:7307-7317). Legacy spreads the
  // EXISTING motor_params blob (`...self.model_data.motor_params`) FIRST, then
  // the chosen motor row, so blob-only keys the motor row lacks survive —
  // notably motor_shaft_grounding_id, which has NO DB column and lives only in
  // this blob (its unprefixed key is `shaft_grounding_id`). The React select
  // path resolves the motor via currentModelSet (motorChoosen is null at the
  // sole call site), so the blob is sourced from the carried-forward stored
  // blob and the live Shaft-Grounding edit is re-stamped from state; the reopen
  // seed round-trips it through s.motor_shaft_grounding_id (FanSelector.tsx:898
  // reads motorParams["shaft_grounding_id"]). Without this, editing Shaft
  // Grounding after reopen was silently lost on re-Select. Rebuild only when
  // there is a motor context (a chosen motor OR a stored blob) — mirroring
  // legacy's `if (motor_choosen)` guard so a motorless fan writes nothing.
  const motorParamsBlob: Record<string, any> =
    typeof m.motor_params === 'string' && m.motor_params
      ? (() => { try { return JSON.parse(m.motor_params) || {}; } catch { return {}; } })()
      : {};
  if (motorChoosen || Object.keys(motorParamsBlob).length > 0) {
    // legacy stamps speed_nominal from the chosen motor, else live state; keep
    // the stored-blob value as a last resort so an unset state can't null it.
    const speedNominal =
      (motorChoosen && motorChoosen.speed_nominal_id) ||
      s.motor_speed_nominal_id ||
      motorParamsBlob.speed_nominal_id;
    dataForSet.motor_params = JSON.stringify({
      ...motorParamsBlob,
      ...(motorChoosen || {}),
      // live Shaft-Grounding edit wins; blob value kept when state is unset.
      ...(s.motor_shaft_grounding_id != null
        ? { shaft_grounding_id: toNum(s.motor_shaft_grounding_id) }
        : {}),
      speed_nominal_id: speedNominal,
      motor_speed_nominal_id: speedNominal,
    });
    if (motorChoosen && motorChoosen.id) dataForSet.master_motor_id = motorChoosen.id;
  }
  // fan_params is a FULL REWRITE of the rebuilt params_fan (legacy chosen-row
  // path, js:6214-6224 — no merge over the stored blob; getModelFanParams
  // resets params_fan={} at js:5090 and everything the blob carries is
  // restamped). The earlier "unknown-key preservation" merge predates the P1b
  // buildModelFanParams port — with `pf` now rebuilt exactly like legacy,
  // merging the stale stored blob back in would keep keys legacy drops (e.g.
  // an old wheel_size_id the new chosen row no longer has) and over-filter
  // the fan form's master_fan_id catalogue. has_vfd is stamped from the LIVE
  // state (js:6217 chosen-row path / :6228 fallback path — both stamp it).
  dataForSet.fan_params = JSON.stringify({
    ...pf,
    has_vfd: !!s.has_vfd,
    style_id: m.style_id || choosen.style_id,
    wheel_mtl_id: m.wheel_mtl_id || choosen.wheel_mtl_id,
    amca_class_id: s.amca_class_id,
  });

  // current_model.set merges motorsForSave + model (4869-4872).
  const currentModelSet: Record<string, unknown> = {
    master_motor_id: motorChoosen ? toNum(motorChoosen.id) : null,
    ...m,
  };

  // Carry the selection itself in the POST (see SELECT_RESULT_KEYS) — the
  // same values the auto-save PATCH sends. Skip undefined (key never set) and
  // NaN (would serialize to null and wipe the stored column).
  for (const k of SELECT_RESULT_KEYS) {
    if (k in dataForSet) continue;
    const v = currentModelSet[k];
    if (v === undefined || (typeof v === 'number' && Number.isNaN(v))) continue;
    dataForSet[k] = v;
  }
  for (const k of SELECT_REGISTRY_KEYS) {
    if (choosen[k]) dataForSet[k] = choosen[k];
  }

  return { currentModelSet, isolatorSet, dataForSet };
}
